/* global Image, FileReader, navigator, DOMMatrix, DOMParser, XMLSerializer, File, Blob, Promise, fetch, Uint8Array, TextDecoder, Response, DecompressionStream, setTimeout, clearTimeout, window, document, console */
/**
 * Vector editor — edits SVG and PDF / PDF-compatible AI attachments in the browser.
 *
 * The artwork is kept as a real SVG DOM (so the group hierarchy, ids, gradients,
 * clip paths … survive an edit round-trip) inside a Shadow DOM, which isolates
 * any <style> the file brings along. PDFs are converted to SVG with pdf.js
 * (SVGGraphics). Everything imported is sanitised (no scripts, event handlers,
 * foreignObject or external references).
 *
 * Static API:
 *   VectorEditorView.open({
 *     url,            // raw file url (SVG / PDF / AI)
 *     fileName,       // suggested file name
 *     title,
 *     converter,      // optional function(url, fileName) -> Promise<{bytes|ArrayBuffer, fileName}>
 *                     // hook for EPS / old AI / CDR (must resolve with SVG or PDF bytes)
 *     onSave          // function(File svgOrPdf)
 *   });
 */
define('vectorEditorView',[
  'module',
  'backbone',
  'marionette',
  'underscore',
  'jquery',
  'imageEditorView',
  'template!vectorEditorView'
], function (
  Module,
  Backbone,
  Marionette,
  _,
  $,
  ImageEditorView,
  Tpl
) {
  'use strict';

  var SVG_NS = 'http://www.w3.org/2000/svg';
  var XLINK_NS = 'http://www.w3.org/1999/xlink';
  var XML_NS = 'http://www.w3.org/XML/1998/namespace';
  var PDFJS_WORKER = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.2.228/pdf.worker.min.js';
  var JSPDF_URL = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
  var OPENTYPE_URL = 'https://cdnjs.cloudflare.com/ajax/libs/opentype.js/1.3.4/opentype.min.js';
  var SVG2PDF_URL = 'https://cdn.jsdelivr.net/npm/svg2pdf.js@2.2.4/dist/svg2pdf.umd.min.js';
  var MAX_UNDO = 40;
  var MAX_TREE_CHILDREN = 200;
  var MAX_COLOR_SHAPES = 4000;
  var HANDLE = 9;
  var TEAL = '#5a63b0';

  function toSet(str) {
    var o = {};
    _.each(str.split(/\s+/), function (k) { if (k) { o[k] = true; } });
    return o;
  }

  var ALLOWED = toSet('svg g defs symbol use path rect circle ellipse line polyline polygon text tspan textPath image ' +
    'clipPath mask linearGradient radialGradient stop pattern style title desc marker filter ' +
    'feBlend feColorMatrix feComponentTransfer feComposite feConvolveMatrix feDiffuseLighting feDisplacementMap ' +
    'feDistantLight feDropShadow feFlood feFuncA feFuncB feFuncG feFuncR feGaussianBlur feImage feMerge feMergeNode ' +
    'feMorphology feOffset fePointLight feSpecularLighting feSpotLight feTile feTurbulence a switch');
  var RENDER = toSet('g path rect circle ellipse line polyline polygon text image use svg');
  var SHAPE = toSet('path rect circle ellipse line polyline polygon text tspan textPath use');
  var CONTAINER = toSet('g svg');
  var KEEP_TEXT_IN = toSet('text tspan textPath style title desc');
  var SHAPE_SELECTOR = 'path,rect,circle,ellipse,line,polyline,polygon,text,tspan,textPath,use';
  var INHERIT = ['fill', 'stroke', 'stroke-width', 'stroke-opacity', 'fill-opacity', 'fill-rule', 'clip-rule',
    'stroke-linecap', 'stroke-linejoin', 'stroke-miterlimit', 'stroke-dasharray', 'stroke-dashoffset',
    'font-family', 'font-size', 'font-weight', 'font-style', 'text-anchor', 'visibility', 'letter-spacing', 'word-spacing'];
  var BAKE = ['fill', 'stroke', 'stroke-width', 'stroke-opacity', 'fill-opacity', 'fill-rule', 'stroke-linecap',
    'stroke-linejoin', 'stroke-miterlimit', 'stroke-dasharray', 'stroke-dashoffset', 'font-family', 'font-size',
    'font-weight', 'font-style', 'text-anchor'];
  var HANDLE_POS = {
    nw: [0, 0], n: [0.5, 0], ne: [1, 0], e: [1, 0.5], se: [1, 1], s: [0.5, 1], sw: [0, 1], w: [0, 0.5]
  };
  var HANDLE_CURSOR = {
    nw: 'nwse-resize', se: 'nwse-resize', ne: 'nesw-resize', sw: 'nesw-resize',
    n: 'ns-resize', s: 'ns-resize', e: 'ew-resize', w: 'ew-resize'
  };
  var DATA_RE = /data:[a-z0-9.+\-\/]+(?:;[a-z0-9\-=.]+)*;base64,[A-Za-z0-9+\/=]{200,}/gi;
  var checkerCss = 'conic-gradient(#e4e8e8 25%, #fff 0 50%, #e4e8e8 0 75%, #fff 0) 0 0 / 16px 16px';

  function t(key, def) { return _.i18n(key, null, def); }

  /* ------------------------------------------------------------------ */
  /* Import: sanitising                                                  */
  /* ------------------------------------------------------------------ */

  function cleanCss(css) {
    return String(css)
      .replace(/@import[^;]*;?/gi, '')
      .replace(/expression\s*\(/gi, '(')
      .replace(/-moz-binding\s*:[^;}]*/gi, '')
      .replace(/behavior\s*:[^;}]*/gi, '')
      .replace(/url\(\s*(?!['"]?(?:data:|#))[^)]*\)/gi, 'none');
  }

  function cleanValue(value) {
    if (/^\s*javascript:/i.test(value)) { return null; }
    if (/url\(\s*(?!['"]?(?:data:|#))/i.test(value)) { return null; }
    return value;
  }

  function copyAttributes(src, el, stats, isImage) {
    var i, attr, an, ns, v;
    for (i = 0; i < src.attributes.length; i++) {
      attr = src.attributes[i];
      an = attr.localName;
      ns = attr.namespaceURI;
      v = attr.value;
      if (/^on/i.test(an)) { continue; }
      if (attr.name === 'xmlns' || /^xmlns:/.test(attr.name)) { continue; }
      if (ns && ns !== XLINK_NS && ns !== XML_NS) { continue; }
      if (an === 'href') {
        if (/^\s*#/.test(v) || (isImage && /^\s*data:image\//i.test(v))) {
          v = v.replace(/^\s+/, '');
        } else {
          stats.externalRefs++;
          continue;
        }
      } else if (an === 'style') {
        v = cleanCss(v);
      } else {
        v = cleanValue(v);
        if (v === null) { continue; }
      }
      if (ns === XLINK_NS) {
        el.setAttributeNS(XLINK_NS, 'xlink:' + an, v);
      } else if (ns === XML_NS) {
        el.setAttributeNS(XML_NS, 'xml:' + an, v);
      } else {
        el.setAttribute(an, v);
      }
    }
  }

  function cloneClean(src, parent, stats) {
    var child, name, el, first, parentName = parent.localName;
    first = true;
    for (child = src.firstChild; child; child = child.nextSibling) {
      if (child.nodeType === 3 || child.nodeType === 4) {
        if (KEEP_TEXT_IN[parentName]) {
          parent.appendChild(document.createTextNode(parentName === 'style' ? cleanCss(child.nodeValue) : child.nodeValue));
        }
        continue;
      }
      if (child.nodeType !== 1) { continue; }
      if (child.namespaceURI !== SVG_NS) { stats.removed++; continue; }
      name = child.localName;
      if (!ALLOWED[name]) {
        stats.removed++;
        continue;
      }
      if (src.localName === 'switch' && !first) { continue; }
      first = false;
      if (name === 'a' || name === 'switch') { name = 'g'; }
      el = document.createElementNS(SVG_NS, name);
      copyAttributes(child, el, stats, name === 'image' || name === 'feImage');
      if ((name === 'image' || name === 'use') && !el.hasAttribute('href') && !el.hasAttributeNS(XLINK_NS, 'href')) { continue; }
      parent.appendChild(el);
      cloneClean(child, el, stats);
    }
  }

  function parseSvgText(text) {
    var doc = new DOMParser().parseFromString(text, 'image/svg+xml');
    var stats = { removed: 0, externalRefs: 0 };
    var src, root;
    if (doc.getElementsByTagName('parsererror').length) {
      throw new Error(t('vectorEditor.parseError', 'This file is not a valid SVG document.'));
    }
    src = doc.documentElement;
    if (!src || src.localName !== 'svg') {
      throw new Error(t('vectorEditor.notSvg', 'This file does not contain an SVG image.'));
    }
    root = document.createElementNS(SVG_NS, 'svg');
    copyAttributes(src, root, stats, false);
    cloneClean(src, root, stats);
    return { root: root, stats: stats };
  }

  /* ------------------------------------------------------------------ */
  /* Small helpers                                                       */
  /* ------------------------------------------------------------------ */

  function parseLen(v) {
    var m, n, u, k;
    if (!v) { return null; }
    m = /^\s*([0-9.]+)\s*(px|pt|pc|mm|cm|in|em)?\s*$/i.exec(v);
    if (!m) { return null; }
    n = parseFloat(m[1]);
    u = (m[2] || 'px').toLowerCase();
    k = { px: 1, pt: 96 / 72, pc: 16, mm: 96 / 25.4, cm: 96 / 2.54, 'in': 96, em: 16 }[u];
    return n * k;
  }

  function fmt(n) { return String(+n.toFixed(6)); }

  function toDom(m) { return new DOMMatrix([m.a, m.b, m.c, m.d, m.e, m.f]); }
  function mTranslate(x, y) { return new DOMMatrix([1, 0, 0, 1, x, y]); }
  function mScale(x, y) { return new DOMMatrix([x, 0, 0, y, 0, 0]); }
  function mRotate(rad) {
    var c = Math.cos(rad), s = Math.sin(rad);
    return new DOMMatrix([c, s, -s, c, 0, 0]);
  }

  function getM(el) {
    var list = el.transform && el.transform.baseVal, c;
    if (!list || !list.numberOfItems) { return new DOMMatrix(); }
    c = list.consolidate();
    return c ? toDom(c.matrix) : new DOMMatrix();
  }

  function setM(el, m) {
    if (m.isIdentity) {
      el.removeAttribute('transform');
      return;
    }
    el.setAttribute('transform', 'matrix(' + [m.a, m.b, m.c, m.d, m.e, m.f].map(fmt).join(' ') + ')');
  }

  function screenM(el) {
    var m = el.getScreenCTM && el.getScreenCTM();
    return m ? toDom(m) : new DOMMatrix();
  }

  function safeInverse(m) {
    try {
      return m.inverse();
    } catch (err) {
      return new DOMMatrix();
    }
  }

  function toHex(c) {
    var m = /rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/.exec(c || ''), i, out = '#', h;
    if (!m) {
      return /^#[0-9a-f]{6}$/i.test(c || '') ? c.toLowerCase() : null;
    }
    for (i = 1; i <= 3; i++) {
      h = parseInt(m[i], 10).toString(16);
      out += h.length === 1 ? '0' + h : h;
    }
    return out;
  }

  function kids(parent) {
    return Array.prototype.slice.call(parent.children);
  }

  function renderableKids(parent) {
    return _.filter(kids(parent), function (el) { return RENDER[el.localName]; });
  }

  function isLocked(el) { return el.getAttribute('data-ve-locked') === '1'; }
  function isHidden(el) { return el.getAttribute('data-ve-hidden') === '1'; }

  function docOrder(els) {
    return els.slice().sort(function (a, b) {
      return (a.compareDocumentPosition(b) & 4) ? -1 : 1;
    });
  }

  function shapesIn(el) {
    var list = SHAPE[el.localName] ? [el] : [];
    return list.concat(Array.prototype.slice.call(el.querySelectorAll(SHAPE_SELECTOR)));
  }

  function svgNode(name, attrs) {
    var el = document.createElementNS(SVG_NS, name);
    _.each(attrs || {}, function (v, k) { el.setAttribute(k, v); });
    return el;
  }

  function rectUnion(rects) {
    var r = { l: Infinity, t: Infinity, r: -Infinity, b: -Infinity };
    _.each(rects, function (x) {
      r.l = Math.min(r.l, x.left);
      r.t = Math.min(r.t, x.top);
      r.r = Math.max(r.r, x.right);
      r.b = Math.max(r.b, x.bottom);
    });
    return r;
  }

  function emptyRect(r) {
    return r.width === 0 && r.height === 0 && r.left === 0 && r.top === 0;
  }

  function loadScript(url) {
    return new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = url;
      s.onload = function () { resolve(); };
      s.onerror = function () { reject(new Error('Failed to load ' + url)); };
      document.head.appendChild(s);
    });
  }

  // jsPDF / svg2pdf ship as UMD; with RequireJS on the page they would register
  // as anonymous AMD modules instead of globals — hide `define.amd` while loading.
  function loadGlobalScripts(urls) {
    var d = window.define, amd = d && d.amd;
    var chain = Promise.resolve();
    if (amd) { d.amd = undefined; }
    _.each(urls, function (u) {
      chain = chain.then(function () { return loadScript(u); });
    });
    return chain.then(function () {
      if (amd) { d.amd = amd; }
    }, function (err) {
      if (amd) { d.amd = amd; }
      throw err;
    });
  }

  function ensurePdfLibs(needOpenType) {
    var urls = [];
    if (needOpenType && !window.opentype) { urls.push(OPENTYPE_URL); }
    if (!window.jspdf) { urls.push(JSPDF_URL); }
    if (!window.svg2pdf) { urls.push(SVG2PDF_URL); }
    return urls.length ? loadGlobalScripts(urls) : Promise.resolve();
  }

  var dataList = [];
  var dataIndex = {};

  function packData(str) {
    return str.replace(DATA_RE, function (m) {
      var id = dataIndex[m];
      if (id === undefined) {
        id = dataList.push(m) - 1;
        dataIndex[m] = id;
      }
      return '@@VEDATA' + id + '@@';
    });
  }

  function unpackData(str) {
    return str.replace(/@@VEDATA(\d+)@@/g, function (m, id) { return dataList[parseInt(id, 10)]; });
  }

  function ascii(bytes, n) {
    var s = '', i, len = Math.min(n, bytes.length);
    for (i = 0; i < len; i++) { s += String.fromCharCode(bytes[i]); }
    return s;
  }

  function gunzip(bytes) {
    var stream = new Response(new Blob([bytes]).stream().pipeThrough(new DecompressionStream('gzip')));
    return stream.arrayBuffer().then(function (buf) { return new Uint8Array(buf); });
  }

  // jsPDF only ships the 14 standard PDF fonts; map whatever the artwork asks
  // for onto the closest of Helvetica / Times / Courier.
  function pdfFont(family) {
    var f = (family || '').toLowerCase();
    if (/courier|mono|consolas|menlo/.test(f)) { return 'courier'; }
    if (/times|georgia|garamond|palatino|cambria/.test(f) && !/sans/.test(f)) { return 'times'; }
    if (/(^|[\s,"'])serif/.test(f) && !/sans/.test(f)) { return 'times'; }
    return 'helvetica';
  }

  var CLIP_OK = toSet('path rect circle ellipse polygon polyline text use');
  var NON_RENDERED = toSet('defs clipPath mask pattern marker symbol linearGradient radialGradient filter');

  function inNonRendered(el) {
    var p = el.parentNode;
    while (p) {
      if (NON_RENDERED[p.localName]) { return true; }
      p = p.parentNode;
    }
    return false;
  }

  // Clipping masks, masks and filters on a group change the look when it is dissolved.
  function hasClipEffect(g) {
    var sty = g.style;
    return !!(g.getAttribute('clip-path') || g.getAttribute('mask') || g.getAttribute('filter') ||
      sty.getPropertyValue('clip-path') || sty.getPropertyValue('mask') || sty.getPropertyValue('filter'));
  }

  var BASE_COLORS = ['#000000', '#ffffff', '#7f7f7f', '#c0c0c0', '#e74c3c', '#e67e22', '#f1c40f', '#2ecc71', '#1abc9c', '#277e80',
    '#3498db', '#1f3a93', '#9b59b6', '#e84393', '#8d6e63', '#ff7675', '#ffd166', '#a3e635', '#38bdf8', '#312e81'];

  function hexRgb(hex) {
    return [parseInt(hex.substr(1, 2), 16), parseInt(hex.substr(3, 2), 16), parseInt(hex.substr(5, 2), 16)];
  }

  function rgbDist2(a, b) {
    var dr = a[0] - b[0], dg = a[1] - b[1], db = a[2] - b[2];
    return dr * dr + dg * dg + db * db;
  }

  // Merge `items` ({hex, w}) into n clusters (weighted k-means, farthest-first
  // seeding). Every cluster is represented by its heaviest member so the result
  // only uses colors that already exist in the artwork.
  function reducePalette(items, n) {
    var pts = _.map(items, function (it) { return { hex: it.hex, c: hexRgb(it.hex), w: Math.max(1, it.w) }; });
    var centers = [], i, k, iter, best, bestScore, score, d, p, groups, sums, map = {}, palette = [];
    centers.push(_.max(pts, function (q) { return q.w; }).c);
    while (centers.length < n) {
      best = null;
      bestScore = -1;
      for (i = 0; i < pts.length; i++) {
        d = Infinity;
        for (k = 0; k < centers.length; k++) { d = Math.min(d, rgbDist2(pts[i].c, centers[k])); }
        score = d * Math.sqrt(pts[i].w);
        if (score > bestScore) {
          bestScore = score;
          best = pts[i];
        }
      }
      centers.push(best.c);
    }
    for (iter = 0; iter < 6; iter++) {
      sums = [];
      for (k = 0; k < centers.length; k++) { sums.push([0, 0, 0, 0]); }
      for (i = 0; i < pts.length; i++) {
        p = pts[i];
        best = 0;
        d = Infinity;
        for (k = 0; k < centers.length; k++) {
          score = rgbDist2(p.c, centers[k]);
          if (score < d) {
            d = score;
            best = k;
          }
        }
        sums[best][0] += p.c[0] * p.w;
        sums[best][1] += p.c[1] * p.w;
        sums[best][2] += p.c[2] * p.w;
        sums[best][3] += p.w;
      }
      for (k = 0; k < centers.length; k++) {
        if (sums[k][3] > 0) { centers[k] = [sums[k][0] / sums[k][3], sums[k][1] / sums[k][3], sums[k][2] / sums[k][3]]; }
      }
    }
    groups = [];
    for (k = 0; k < centers.length; k++) { groups.push([]); }
    for (i = 0; i < pts.length; i++) {
      p = pts[i];
      best = 0;
      d = Infinity;
      for (k = 0; k < centers.length; k++) {
        score = rgbDist2(p.c, centers[k]);
        if (score < d) {
          d = score;
          best = k;
        }
      }
      groups[best].push(p);
    }
    _.each(groups, function (g) {
      var rep;
      if (!g.length) { return; }
      rep = _.max(g, function (q) { return q.w; });
      palette.push(rep.hex);
      _.each(g, function (q) { map[q.hex] = rep.hex; });
    });
    return { map: map, palette: palette };
  }

  var FONTS = ['Arial, Helvetica, sans-serif', 'Verdana, Geneva, sans-serif', 'Tahoma, sans-serif', '"Trebuchet MS", sans-serif',
    'Impact, sans-serif', 'Georgia, serif', '"Times New Roman", Times, serif', '"Courier New", Courier, monospace', '"Comic Sans MS", cursive'];
  var DEFAULT_FILL = '#3498db';

  // Shared between editor instances so objects can be copied from one file into another.
  var clipboardStore = null;

  function firstFamily(fam) {
    return String(fam || '').split(',')[0].replace(/^[\s"']+|[\s"']+$/g, '');
  }

  function rgbToCmyk(hex) {
    var c = hexRgb(hex), r = c[0] / 255, g = c[1] / 255, b = c[2] / 255, k = 1 - Math.max(r, g, b);
    if (k >= 1) { return [0, 0, 0, 100]; }
    return [(1 - r - k) / (1 - k), (1 - g - k) / (1 - k), (1 - b - k) / (1 - k), k].map(function (v) { return Math.round(v * 100); });
  }

  // Plain (profile-free) conversion — fine for picking a colour, not a substitute for a print profile.
  function cmykToHex(c, m, y, k) {
    function f(v) { return Math.max(0, Math.min(100, parseFloat(v) || 0)) / 100; }
    var out = '#', rgb, i, h;
    c = f(c); m = f(m); y = f(y); k = f(k);
    rgb = [255 * (1 - c) * (1 - k), 255 * (1 - m) * (1 - k), 255 * (1 - y) * (1 - k)];
    for (i = 0; i < 3; i++) {
      h = Math.round(rgb[i]).toString(16);
      out += h.length === 1 ? '0' + h : h;
    }
    return out;
  }

  function cmykLabel(hex) {
    var c = rgbToCmyk(hex);
    return 'C' + c[0] + ' M' + c[1] + ' Y' + c[2] + ' K' + c[3];
  }

  function textNodes(t) {
    return [t].concat(Array.prototype.slice.call(t.querySelectorAll('tspan,textPath')));
  }

  function textContentOf(t) {
    var lines = Array.prototype.filter.call(t.children, function (c) { return c.getAttribute('data-ve-line') === '1'; });
    if (lines.length) {
      return _.map(lines, function (l) { return l.textContent === '​' ? '' : l.textContent; }).join('\n');
    }
    return t.textContent;
  }

  function setTextContent(t, str) {
    var lines = String(str).split(/\r?\n/), x = (t.getAttribute('x') || '0').split(/[\s,]+/)[0], i, ts;
    while (t.firstChild) { t.removeChild(t.firstChild); }
    if (lines.length === 1) {
      t.appendChild(document.createTextNode(lines[0]));
      return;
    }
    for (i = 0; i < lines.length; i++) {
      ts = svgNode('tspan', { x: x, dy: i ? '1.2em' : '0', 'data-ve-line': '1' });
      ts.textContent = lines[i] === '' ? '​' : lines[i];
      t.appendChild(ts);
    }
  }

  function refsIn(node) {
    var ids = [], all = [node].concat(Array.prototype.slice.call(node.querySelectorAll('*'))), i, j, v, m, attr, re = /url\(\s*['"]?#([^)'"\s]+)/g;
    for (i = 0; i < all.length; i++) {
      for (j = 0; j < all[i].attributes.length; j++) {
        attr = all[i].attributes[j];
        v = attr.value;
        if (attr.localName === 'href' && v.charAt(0) === '#') {
          ids.push(v.slice(1));
        } else if (v.indexOf('url(') !== -1) {
          re.lastIndex = 0;
          while ((m = re.exec(v))) { ids.push(m[1]); }
        }
      }
    }
    return ids;
  }

  function sameM(a, b) {
    return _.every(['a', 'b', 'c', 'd', 'e', 'f'], function (k) { return Math.abs(a[k] - b[k]) < 1e-4 * (1 + Math.abs(a[k])); });
  }

  // Text from a PDF is stored as character codes of the PDF's own embedded (subset) font, which
  // no standard PDF font can draw. Draw those glyphs as outlines taken from the embedded font.
  function parseEmbeddedFonts(css) {
    var fonts = {}, re = /@font-face\s*\{([^}]*)\}/g, m, fam, data, bin, bytes, i;
    while ((m = re.exec(css))) {
      fam = /font-family:\s*"?([^";]+)"?/.exec(m[1]);
      data = /url\(\s*["']?data:[^;,]+;base64,([A-Za-z0-9+\/=]+)/.exec(m[1]);
      if (!fam || !data) { continue; }
      try {
        bin = window.atob(data[1]);
        bytes = new Uint8Array(bin.length);
        for (i = 0; i < bin.length; i++) { bytes[i] = bin.charCodeAt(i); }
        fonts[fam[1].replace(/^\s+|\s+$/g, '')] = window.opentype.parse(bytes.buffer);
      } catch (err) {
        console.warn('Vector editor: could not read embedded font ' + fam[1], err);
      }
    }
    return fonts;
  }

  // inline style wins over the presentation attribute, the span over its text element
  function pick(span, text, name) {
    return span.style.getPropertyValue(name) || span.getAttribute(name) || text.style.getPropertyValue(name) || text.getAttribute(name) || '';
  }

  // Drop @font-face rules whose font no remaining text uses (after converting text to outlines).
  function pruneFontFaces(svg) {
    var used = {};
    _.each(Array.prototype.slice.call(svg.querySelectorAll('text')), function (tx) {
      if (!tx.textContent.length) {
        tx.parentNode.removeChild(tx);   // empty text runs PDF import leaves behind
        return;
      }
      _.each(textNodes(tx), function (n) {
        var f = n.textContent.length ? firstFamily(n.style.getPropertyValue('font-family') || n.getAttribute('font-family')) : '';
        if (f) { used[f] = true; }
      });
    });
    _.each(Array.prototype.slice.call(svg.querySelectorAll('style')), function (st) {
      st.textContent = st.textContent.replace(/@font-face\s*\{[^}]*\}/g, function (rule) {
        var m = /font-family:\s*"?([^";]+)"?/.exec(rule);
        return m && !used[m[1].replace(/^\s+|\s+$/g, '')] ? '' : rule;
      });
      if (!/\S/.test(st.textContent)) { st.parentNode.removeChild(st); }
    });
  }

  function outlineEmbeddedText(svgOrTexts, fonts) {
    var done = 0, groups = [];
    _.each(_.isArray(svgOrTexts) ? svgOrTexts : Array.prototype.slice.call(svgOrTexts.querySelectorAll('text')), function (tx) {
      var g = svgNode('g'), leftovers = [], converted = false, spans = _.filter(kids(tx), function (c) { return c.localName === 'tspan'; }), rest, i, a;
      if (!spans.length) { return; }
      _.each(spans, function (sp) {
        var fam = firstFamily(pick(sp, tx, 'font-family')), font = fonts[fam], txt = sp.textContent;
        var size = parseFloat(pick(sp, tx, 'font-size')) || 0, xs, y, cursor, d = [], k, gl, x, path;
        if (!txt) { return; }
        if (!font || !size) {
          leftovers.push(sp);
          return;
        }
        xs = _.map(_.filter((sp.getAttribute('x') || '').split(/[\s,]+/), function (n) { return n !== ''; }), parseFloat);
        y = parseFloat(sp.getAttribute('y') || tx.getAttribute('y') || '0') || 0;
        cursor = xs.length ? xs[0] : (parseFloat(tx.getAttribute('x') || '0') || 0);
        for (k = 0; k < txt.length; k++) {
          gl = font.charToGlyph(txt.charAt(k));
          x = xs.length === txt.length ? xs[k] : cursor;
          d.push(gl.getPath(x, y, size).toPathData(5));
          cursor = x + gl.advanceWidth * size / font.unitsPerEm;
        }
        path = svgNode('path', { d: d.join(' ') });
        _.each(['fill', 'fill-opacity', 'stroke', 'stroke-width', 'stroke-opacity'], function (n) {
          var v = pick(sp, tx, n);
          if (v) { path.setAttribute(n, v); }
        });
        g.appendChild(path);
        converted = true;
        done++;
      });
      if (!converted) { return; }
      for (i = 0; i < tx.attributes.length; i++) {
        a = tx.attributes[i];
        if (!/^(x|y|dx|dy|xml:space|text-anchor|font-.*|letter-spacing|word-spacing)$/.test(a.name)) { g.setAttribute(a.name, a.value); }
      }
      if (leftovers.length) {
        rest = tx.cloneNode(false);
        rest.removeAttribute('transform');
        _.each(leftovers, function (sp) { rest.appendChild(sp.cloneNode(true)); });
        g.appendChild(rest);
      }
      tx.parentNode.replaceChild(g, tx);
      groups.push(g);
    });
    return { done: done, groups: groups };
  }

  function typeLabel(el) {
    var map = {
      g: t('vectorEditor.typeGroup', 'Group'), path: t('vectorEditor.typePath', 'Path'),
      rect: t('vectorEditor.typeRect', 'Rectangle'), circle: t('vectorEditor.typeCircle', 'Circle'),
      ellipse: t('vectorEditor.typeEllipse', 'Ellipse'), line: t('vectorEditor.typeLine', 'Line'),
      polyline: t('vectorEditor.typePolyline', 'Polyline'), polygon: t('vectorEditor.typePolygon', 'Polygon'),
      text: t('vectorEditor.typeText', 'Text'), image: t('vectorEditor.typeImage', 'Image'),
      use: t('vectorEditor.typeUse', 'Instance'), svg: t('vectorEditor.typeSvg', 'Nested SVG')
    };
    var base = map[el.localName] || el.localName, txt;
    if (el.localName === 'text') {
      txt = (el.textContent || '').replace(/\s+/g, ' ').replace(/^\s|\s$/g, '');
      if (txt) { base += ': ' + (txt.length > 24 ? txt.slice(0, 24) + '…' : txt); }
    } else if (el.getAttribute('id')) {
      base += ' #' + el.getAttribute('id');
    }
    return base;
  }

  /* ------------------------------------------------------------------ */
  /* View                                                                */
  /* ------------------------------------------------------------------ */

  var View = Marionette.LayoutView.extend({
    template: Tpl,
    className: 'vector-editor-view',

    ui: {
      stage: '.ve-stage',
      docHost: '.ve-doc-host',
      loader: '.ve-loader',
      error: '.ve-error',
      breadcrumb: '.ve-breadcrumb',
      crumbText: '.ve-crumb-text',
      colorsList: '.ve-colors-list',
      colorsScope: '.ve-colors-scope',
      tree: '.ve-tree',
      selInfo: '.ve-selection-info',
      footerNote: '.ve-footer-note',
      zoomLabel: '.ve-zoom-label',
      pageSelect: '.ve-page-select',
      pagePick: '.ve-page-pick',
      ctx: '.ve-ctx-menu',
      ctxLockLabel: '.ve-ctx-lock-label',
      ctxBadge: '.ve-ctx-badge',
      fill: '.ve-fill',
      fillNone: '.ve-fill-none',
      stroke: '.ve-stroke',
      strokeNone: '.ve-stroke-none',
      strokeWidth: '.ve-stroke-width',
      alpha: '.ve-alpha',
      alphaVal: '.ve-alpha-val',
      ctxAlpha: '.ve-ctx-alpha',
      ctxAlphaVal: '.ve-ctx-alpha-val',
      textPanel: '.ve-panel-text',
      textContent: '.ve-text-content',
      fontFamily: '.ve-font',
      fontSize: '.ve-font-size',
      letterSpacing: '.ve-letter-spacing',
      geoPanel: '.ve-panel-geometry',
      geoX: '.ve-geo-x',
      geoY: '.ve-geo-y',
      geoW: '.ve-geo-w',
      geoH: '.ve-geo-h',
      geoLock: '.ve-geo-lock',
      geoRadiusRow: '.ve-geo-radius-row',
      geoRadius: '.ve-geo-radius',
      geoUnit: '.ve-geo-unit',
      abPanel: '.ve-panel-artboard',
      abPreset: '.ve-ab-preset',
      abW: '.ve-ab-w',
      abH: '.ve-ab-h',
      abLock: '.ve-ab-lock',
      abScale: '.ve-ab-scale',
      abUnit: '.ve-ab-unit',
      abMargin: '.ve-ab-margin',
      abMarginUnit: '.ve-ab-margin-unit',
      pop: '.ve-color-pop',
      popTitle: '.ve-pop-title',
      popColor: '.ve-pop-color',
      popHex: '.ve-pop-hex',
      popNone: '.ve-pop-none',
      popBase: '.ve-pop-base',
      popUsed: '.ve-pop-used',
      popAll: '.ve-pop-all',
      popTolRow: '.ve-pop-tol-row',
      popTol: '.ve-pop-tol',
      popTolVal: '.ve-pop-tol-val',
      repNone: '.ve-rep-none',
      repUsed: '.ve-rep-used',
      replacePanel: '.ve-panel-replace',
      reducePanel: '.ve-panel-reduce',
      repScope: '.ve-rep-scope',
      repList: '.ve-rep-list',
      repColor: '.ve-rep-color',
      repHex: '.ve-rep-hex',
      repPalette: '.ve-rep-palette',
      repTol: '.ve-rep-tol',
      repTolVal: '.ve-rep-tol-val',
      repApply: '.ve-rep-apply',
      crScope: '.ve-cr-scope',
      crList: '.ve-cr-list',
      crEmpty: '.ve-cr-empty'
    },

    events: {
      'click [data-action]': 'onAction',
      'input @ui.fill': 'onFillInput',
      'change @ui.fill': 'endInteractive',
      'change @ui.fillNone': 'onFillNone',
      'input @ui.stroke': 'onStrokeInput',
      'change @ui.stroke': 'endInteractive',
      'change @ui.strokeNone': 'onStrokeNone',
      'input @ui.strokeWidth': 'onStrokeWidth',
      'change @ui.strokeWidth': 'endInteractive',
      'input @ui.alpha': 'onAlphaInput',
      'change @ui.alpha': 'endInteractive',
      'change @ui.pageSelect': 'onPageChange',
      'input .ve-color-row input[type="color"]': 'onColorRowInput',
      'change .ve-color-row input[type="color"]': 'onColorRowChange',
      'click .ve-node-row': 'onTreeClick',
      'click .ve-rep-row': 'onRepRowClick',
      'click .ve-rep-sw': 'onRepSwatch',
      'input @ui.repColor': 'onRepColorInput',
      'input @ui.repHex': 'onRepHexInput',
      'input @ui.repTol': 'onRepTol',
      'click .ve-cr-item': 'onCrClick',
      'input @ui.textContent': 'onTextContent',
      'change @ui.textContent': 'endInteractive',
      'change @ui.fontFamily': 'onFontFamily',
      'input @ui.fontSize': 'onFontSize',
      'change @ui.fontSize': 'endInteractive',
      'input @ui.letterSpacing': 'onLetterSpacing',
      'change @ui.letterSpacing': 'endInteractive',
      'click [data-fmt]': 'onFmt',
      'click [data-align]': 'onAlign',
      'change @ui.geoX': 'onGeoChange',
      'change @ui.geoY': 'onGeoChange',
      'change @ui.geoW': 'onGeoChange',
      'change @ui.geoH': 'onGeoChange',
      'change @ui.geoRadius': 'onGeoRadius',
      'input .ve-rep-cmyk': 'onRepCmyk',
      'click @ui.repNone': 'onRepNone',
      'change @ui.abPreset': 'onAbPreset',
      'input @ui.abW': 'onAbSide',
      'input @ui.abH': 'onAbSide',
      'change @ui.abScale': 'onAbScale',
      'click .ve-ab-anchor': 'onAbAnchor',
      'click .ve-color-row input[type="color"]': 'onColorRowClick',
      'click @ui.fill': 'onFillClick',
      'click @ui.stroke': 'onStrokeClick',
      'input @ui.popColor': 'onPopColor',
      'input @ui.popHex': 'onPopHex',
      'input .ve-pop-cmyk': 'onPopCmyk',
      'click @ui.popNone': 'onPopNone',
      'click .ve-pop-sw': 'onPopSwatch',
      'change @ui.popAll': 'onPopAll',
      'input @ui.popTol': 'onPopTol',
      'mousedown @ui.stage': 'onStageMouseDown',
      'dblclick @ui.stage': 'onStageDblClick',
      'contextmenu @ui.stage': 'onStageContext'
    },

    initialize: function (options) {
      this.options = options || {};
      this.root = null;
      this.level = null;
      this.selection = [];
      this.vb = { x: 0, y: 0, w: 100, h: 100 };
      this.vbDirty = false;
      this.zoom = 1;
      this.undoStack = [];
      this.redoStack = [];
      this.drag = null;
      this.bgWhite = false;
      this.expanded = [];
      this.pdfDoc = null;
      this.pageNo = 1;
      this.numPages = 1;
      this.pageStash = {};
      this.kind = 'svg';
      this.stats = { removed: 0, externalRefs: 0 };
      this._iact = false;
      this._space = false;
      this._treeRows = [];
      this._colorEntries = [];
      this.unit = 'mm';
      try {
        this.unit = window.localStorage.getItem('ve-unit') === 'px' ? 'px' : 'mm';
      } catch (err) { /* storage blocked: default to mm */ }
      this.mmPerUnit = 25.4 / 96;
      this.exportPxPerUnit = 1;
      this.abAnchor = { x: 0, y: 0 };
      this.printAreas = this.options.printAreas || [];
      this.tool = null;
      this.insertTool = null;
      this._pasteN = 0;
      this._repFrom = null;
      this._crCache = {};
    },

    onShow: function () {
      var view = this;
      var host = this.ui.docHost[0];
      host.style.position = 'relative';
      host.style.background = checkerCss;

      this.artHost = document.createElement('div');
      this.artHost.style.cssText = 'position:absolute;left:0;top:0;width:100%;height:100%;';
      this.shadow = this.artHost.attachShadow({ mode: 'open' });
      this.baseStyle = document.createElement('style');
      // `all: initial` stops the app's font / colour from leaking into the artwork,
      // so it renders like the standalone file would.
      this.baseStyle.textContent = ':host{all:initial;display:block} svg{display:block}';
      this.shadow.appendChild(this.baseStyle);
      host.appendChild(this.artHost);

      this.dimW = this.makeDim(false);
      this.dimH = this.makeDim(true);
      _.each([this.dimW, this.dimH], function (dim) {
        dim.label.className = 've-dim-label';
        dim.label.title = t('vectorEditor.artboardTip', 'Change the size of the artboard');
        dim.label.style.pointerEvents = 'auto';
        dim.label.style.cursor = 'pointer';
        dim.label.addEventListener('mousedown', function (ev) { ev.stopPropagation(); });
        dim.label.addEventListener('click', function () {
          if (view.tool !== 'artboard') { view.setTool('artboard'); }
        });
      });
      host.appendChild(this.dimW.box);
      host.appendChild(this.dimH.box);

      this.overlay = svgNode('svg');
      this.overlay.setAttribute('style', 'position:absolute;left:0;top:0;width:100%;height:100%;overflow:visible;pointer-events:none;');
      host.appendChild(this.overlay);

      this._wheel = _.bind(function (e) {
        if (!(e.ctrlKey || e.metaKey) || !this.root) { return; }
        e.preventDefault();
        this.setZoom(this.zoom * (e.deltaY < 0 ? 1.1 : 1 / 1.1));
      }, this);
      this.ui.stage[0].addEventListener('wheel', this._wheel, { passive: false });

      this._docMove = _.bind(this.onDocMouseMove, this);
      this._docUp = _.bind(this.onDocMouseUp, this);
      this._docDown = _.bind(function (e) {
        if (!$(e.target).closest('.ve-ctx-menu').length) { this.hideCtx(); }
        if (this._pop && !$(e.target).closest('.ve-color-pop,.ve-fill,.ve-stroke,.ve-color-row input').length) { this.closePop(); }
      }, this);
      $(document).on('mousemove', this._docMove);
      $(document).on('mouseup', this._docUp);
      $(document).on('mousedown', this._docDown);

      this._keydown = _.bind(this.onKeyDown, this);
      this._keyup = _.bind(function (e) {
        if (e.key === ' ' || e.code === 'Space') {
          this._space = false;
          this.ui.stage.css('cursor', '');
        }
      }, this);
      document.addEventListener('keydown', this._keydown);
      document.addEventListener('keyup', this._keyup);
      this._copyH = _.bind(function (e) { this.onClipEvent(e, false); }, this);
      this._cutH = _.bind(function (e) { this.onClipEvent(e, true); }, this);
      this._pasteH = _.bind(this.onPasteEvent, this);
      document.addEventListener('copy', this._copyH);
      document.addEventListener('cut', this._cutH);
      document.addEventListener('paste', this._pasteH);
      if (this.printAreas.length) {
        this.ui.abPreset.find('option').first().after('<optgroup label="' + _.escape(t('vectorEditor.printAreas', 'Print areas of this order')) + '">' +
          _.map(this.printAreas, function (pa) {
            return '<option value="' + (Math.round(pa.width * 10) / 10) + 'x' + (Math.round(pa.height * 10) / 10) + '">' + _.escape(pa.label) + '</option>';
          }).join('') + '</optgroup>');
      }
      this.ui.fontFamily.html(_.map(FONTS, function (f) {
        return '<option value="' + _.escape(f) + '">' + _.escape(firstFamily(f)) + '</option>';
      }).join(''));

      // The context menu lives on <body> so the modal's overflow / stacking can never hide it.
      // The host carries the modal class so the (scoped) menu styles still apply on <body>.
      this.ctxHost = $('<div class="vector-editor-modal ve-ctx-host"></div>').css({
        position: 'fixed', left: 0, top: 0, width: 0, height: 0, margin: 0, padding: 0, gap: 0, display: 'block',
        overflow: 'visible', background: 'none', flex: 'none', zIndex: 100000
      }).appendTo(document.body);
      this.ui.ctx.appendTo(this.ctxHost);
      this.ui.ctx.on('click', '[data-ctx]', _.bind(this.onCtx, this));
      this.ui.ctx.on('input', '.ve-ctx-alpha', _.bind(this.onCtxAlphaInput, this));
      this.ui.ctx.on('change', '.ve-ctx-alpha', _.bind(this.endInteractive, this));
      this.ui.ctx.on('mouseenter', '.ve-ctx-parent', function () {
        var sub = this.querySelector('.ve-ctx-sub'), r;
        if (!sub) { return; }
        sub.style.top = '-5px';
        r = sub.getBoundingClientRect();
        if (r.bottom > window.innerHeight - 8) { sub.style.top = (-5 - (r.bottom - window.innerHeight + 8)) + 'px'; }
      });

      this.updateUi();
      this.load();
    },

    onDestroy: function () {
      if (this._wheel && this.ui.stage && this.ui.stage[0]) {
        this.ui.stage[0].removeEventListener('wheel', this._wheel);
      }
      $(document).off('mousemove', this._docMove);
      $(document).off('mouseup', this._docUp);
      $(document).off('mousedown', this._docDown);
      document.removeEventListener('keydown', this._keydown);
      document.removeEventListener('keyup', this._keyup);
      document.removeEventListener('copy', this._copyH);
      document.removeEventListener('cut', this._cutH);
      document.removeEventListener('paste', this._pasteH);
      clearTimeout(this._treeTimer);
      clearTimeout(this._colorTimer);
      if (this.ctxHost) { this.ctxHost.remove(); }
      if (this.fontStyle && this.fontStyle.parentNode) {
        this.fontStyle.parentNode.removeChild(this.fontStyle);
      }
      if (this.pdfDoc && this.pdfDoc.destroy) { this.pdfDoc.destroy(); }
    },

    /* ------------------------------------------------------------------ */
    /* Loading                                                              */
    /* ------------------------------------------------------------------ */

    showLoader: function (show) { this.ui.loader.css('display', show ? 'block' : 'none'); },

    showError: function (msg) {
      this.showLoader(false);
      this.ui.error.text(msg).css('display', 'flex');
    },

    notify: function (message) {
      if ($.notify) {
        $.notify({ message: message }, {
          type: 'info',
          placement: { from: 'top', align: 'center' },
          delay: 3500
        });
      }
    },

    setNote: function (text) { this.ui.footerNote.text(text || ''); },

    load: function () {
      var self = this;
      this.showLoader(true);
      this.fetchBytes(this.options.url).then(function (bytes) {
        return self.detectAndOpen(bytes, self.options.fileName, true);
      }).then(function () {
        self.showLoader(false);
      }).then(null, function (err) {
        console.error('Vector editor load failed', err);
        self.showError((err && err.message) || t('vectorEditor.loadError', 'Could not open this file.'));
      });
    },

    fetchBytes: function (url) {
      return fetch(url).then(function (res) {
        if (!res.ok) { throw new Error(t('vectorEditor.fetchError', 'The file could not be downloaded.')); }
        return res.arrayBuffer();
      }).then(function (buf) {
        var bytes = new Uint8Array(buf);
        if (bytes.length > 2 && bytes[0] === 0x1f && bytes[1] === 0x8b && window.DecompressionStream) {
          return gunzip(bytes);
        }
        return bytes;
      });
    },

    detectAndOpen: function (bytes, fileName, allowConverter) {
      var self = this;
      var head = ascii(bytes, 2048);
      var ext = (/\.([a-z0-9]+)$/i.exec(fileName || '') || [])[1];
      ext = ext ? ext.toLowerCase() : '';
      if (head.indexOf('%PDF-') !== -1) {
        this.kind = 'pdf';
        return this.loadPdf(bytes);
      }
      if (/<svg[\s>]/i.test(head) || ext === 'svg' || ext === 'svgz') {
        this.kind = 'svg';
        this.mountSvgText(new TextDecoder('utf-8').decode(bytes));
        return Promise.resolve();
      }
      if (allowConverter && typeof this.options.converter === 'function') {
        return Promise.resolve(this.options.converter(this.options.url, fileName)).then(function (res) {
          var b = res && (res.bytes || res);
          if (b instanceof ArrayBuffer) { b = new Uint8Array(b); }
          return self.detectAndOpen(b, (res && res.fileName) || fileName, false);
        });
      }
      throw new Error(t('vectorEditor.unsupported',
        'This vector format (EPS, older AI or CDR) cannot be edited in the browser yet. Please convert it to SVG or PDF first.'));
    },

    mountSvgText: function (text) {
      var parsed = parseSvgText(text);
      this.stats = parsed.stats;
      this.undoStack = [];
      this.redoStack = [];
      this.mountRoot(parsed.root);
      this.zoomFit();
      this.refresh({ tree: true });
      this.updateNote();
    },

    loadPdf: function (bytes) {
      var self = this;
      var lib = window['pdfjs-dist/build/pdf'];
      if (!lib || !lib.SVGGraphics) {
        return Promise.reject(new Error(t('vectorEditor.noPdfJs', 'The PDF engine is not available.')));
      }
      lib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER;
      return lib.getDocument({ data: bytes.slice(0) }).promise.then(function (pdf) {
        self.pdfDoc = pdf;
        self.numPages = pdf.numPages;
        self.pageNo = 1;
        self.fillPageSelect();
        return self.renderPdfPage(1);
      });
    },

    fillPageSelect: function () {
      var html = '', i;
      for (i = 1; i <= this.numPages; i++) {
        html += '<option value="' + i + '">' + i + ' / ' + this.numPages + '</option>';
      }
      this.ui.pageSelect.html(html).val(String(this.pageNo));
      this.ui.pagePick.css('display', this.numPages > 1 ? '' : 'none');
    },

    renderPdfPage: function (n) {
      var self = this;
      var lib = window['pdfjs-dist/build/pdf'];
      this.showLoader(true);
      return this.pdfDoc.getPage(n).then(function (page) {
        var viewport = page.getViewport({ scale: 1 });
        return page.getOperatorList().then(function (ops) {
          var gfx = new lib.SVGGraphics(page.commonObjs, page.objs, true);
          gfx.embedFonts = true;
          return gfx.getSVG(ops, viewport);
        });
      }).then(function (svgEl) {
        self.mountSvgText(new XMLSerializer().serializeToString(svgEl));
        self.showLoader(false);
      });
    },

    onPageChange: function () {
      var n = parseInt(this.ui.pageSelect.val(), 10), self = this;
      if (!n || n === this.pageNo || !this.pdfDoc) { return; }
      this.pageStash[this.pageNo] = packData(new XMLSerializer().serializeToString(this.root));
      this.pageNo = n;
      if (this.pageStash[n]) {
        this.undoStack = [];
        this.redoStack = [];
        this.mountSvgText(unpackData(this.pageStash[n]));
        return;
      }
      this.renderPdfPage(n).then(null, function (err) {
        self.showError((err && err.message) || t('vectorEditor.loadError', 'Could not open this file.'));
      });
    },

    updateNote: function () {
      var parts = [this.kind.toUpperCase()], count;
      if (this.root) {
        parts.push(this.fmtSize(this.vb.w, this.vb.h));
        count = this.root.querySelectorAll('*').length;
        parts.push(count + ' ' + t('vectorEditor.elements', 'elements'));
      }
      if (this.numPages > 1) {
        parts.push(t('vectorEditor.pageNote', 'only this page is saved'));
      }
      if (this.stats.removed || this.stats.externalRefs) {
        parts.push(t('vectorEditor.sanitized', 'unsafe or external content was removed'));
      }
      this.setNote(parts.join(' · '));
    },

    /* ------------------------------------------------------------------ */
    /* Document mounting / zoom                                             */
    /* ------------------------------------------------------------------ */

    mountRoot: function (root) {
      var vbAttr, n, w, h, bb;
      if (this.root && this.root.parentNode) { this.root.parentNode.removeChild(this.root); }
      this.root = root;
      this.level = root;
      this.selection = [];
      this.expanded = [];
      this.vbDirty = false;
      this.docW = root.getAttribute('width');
      this.docH = root.getAttribute('height');
      root.setAttribute('width', '100');
      root.setAttribute('height', '100');
      root.style.overflow = 'visible';
      root.style.display = 'block';
      this.shadow.appendChild(root);

      vbAttr = root.getAttribute('viewBox');
      n = vbAttr ? _.map(vbAttr.split(/[\s,]+/), parseFloat) : null;
      if (n && n.length === 4 && n[2] > 0 && n[3] > 0 && _.every(n, isFinite)) {
        this.vb = { x: n[0], y: n[1], w: n[2], h: n[3] };
      } else {
        w = parseLen(this.docW);
        h = parseLen(this.docH);
        if (w && h) {
          this.vb = { x: 0, y: 0, w: w, h: h };
        } else {
          bb = root.getBBox();
          this.vb = { x: bb.x, y: bb.y, w: bb.width || 100, h: bb.height || 100 };
        }
      }
      // Physical size of one user unit: PDFs are in points, SVGs follow their width attribute.
      w = parseLen(this.docW);
      this.exportPxPerUnit = (w && this.vb.w > 0) ? w / this.vb.w : 1;
      if (this.kind === 'pdf') {
        this.mmPerUnit = 25.4 / 72;
      } else if (w && this.vb.w > 0) {
        this.mmPerUnit = w * 25.4 / 96 / this.vb.w;
      } else {
        this.mmPerUnit = 25.4 / 96;
      }
      this.syncFonts();
    },

    // @font-face rules inside a shadow tree are ignored by browsers, so copy
    // them to the document head (embedded PDF fonts, icon fonts …).
    syncFonts: function () {
      var css = '';
      _.each(this.root.querySelectorAll('style'), function (s) {
        var m = (s.textContent || '').match(/@font-face\s*\{[^}]*\}/g);
        if (m) { css += m.join('\n') + '\n'; }
      });
      if (!this.fontStyle) {
        this.fontStyle = document.createElement('style');
        this.fontStyle.setAttribute('data-ve-fonts', '1');
        document.head.appendChild(this.fontStyle);
      }
      this.fontStyle.textContent = css;
    },

    applyZoom: function () {
      var w = this.vb.w * this.zoom, h = this.vb.h * this.zoom;
      var host = this.ui.docHost[0];
      host.style.width = w + 'px';
      host.style.height = h + 'px';
      this.root.setAttribute('viewBox', [this.vb.x, this.vb.y, this.vb.w, this.vb.h].map(fmt).join(' '));
      this.root.setAttribute('width', fmt(w));
      this.root.setAttribute('height', fmt(h));
      this.ui.zoomLabel.text(Math.round(this.zoom * 100) + '%');
      this.updateDims();
      this.drawOverlay();
    },

    setZoom: function (z) {
      var stage = this.ui.stage[0], host = this.ui.docHost[0];
      var cx, cy, u, v;
      z = Math.max(0.02, Math.min(64, z));
      if (!this.root) { return; }
      cx = stage.scrollLeft + stage.clientWidth / 2 - host.offsetLeft;
      cy = stage.scrollTop + stage.clientHeight / 2 - host.offsetTop;
      u = cx / this.zoom;
      v = cy / this.zoom;
      this.zoom = z;
      this.applyZoom();
      stage.scrollLeft = u * z + host.offsetLeft - stage.clientWidth / 2;
      stage.scrollTop = v * z + host.offsetTop - stage.clientHeight / 2;
    },

    zoomFit: function () {
      var stage = this.ui.stage[0];
      var sw = Math.max(100, stage.clientWidth - 72), sh = Math.max(100, stage.clientHeight - 100);
      this.zoom = Math.max(0.02, Math.min(20, Math.min(sw / this.vb.w, sh / this.vb.h)));
      this.applyZoom();
      stage.scrollLeft = 0;
      stage.scrollTop = 0;
    },

    toggleBg: function () {
      this.bgWhite = !this.bgWhite;
      this.ui.docHost[0].style.background = this.bgWhite ? '#fff' : checkerCss;
    },

    /* ------------------------------------------------------------------ */
    /* Undo / redo                                                          */
    /* ------------------------------------------------------------------ */

    pathOf: function (el) {
      var p = [], idx;
      while (el && el !== this.root) {
        idx = kids(el.parentNode).indexOf(el);
        p.unshift(idx);
        el = el.parentNode;
      }
      return p;
    },

    byPath: function (p) {
      var el = this.root, i;
      for (i = 0; i < p.length; i++) {
        el = el && el.children[p[i]];
      }
      return el || null;
    },

    snapshot: function () {
      var self = this;
      return {
        xml: packData(new XMLSerializer().serializeToString(this.root)),
        sel: _.map(this.selection, function (el) { return self.pathOf(el); }),
        level: this.pathOf(this.level),
        vb: _.clone(this.vb),
        vbDirty: this.vbDirty,
        mm: this.mmPerUnit,
        ppu: this.exportPxPerUnit
      };
    },

    checkpoint: function () {
      this.undoStack.push(this.snapshot());
      if (this.undoStack.length > MAX_UNDO) { this.undoStack.shift(); }
      this.redoStack = [];
      this.updateUi();
    },

    restore: function (s) {
      var self = this;
      var doc = new DOMParser().parseFromString(unpackData(s.xml), 'image/svg+xml');
      var imported = document.importNode(doc.documentElement, true);
      if (this.root.parentNode) { this.root.parentNode.removeChild(this.root); }
      this.root = imported;
      this.shadow.appendChild(imported);
      this.vb = _.clone(s.vb);
      this.vbDirty = s.vbDirty;
      if (s.mm) {
        this.mmPerUnit = s.mm;
        this.exportPxPerUnit = s.ppu;
      }
      this.level = this.byPath(s.level) || imported;
      this.selection = _.compact(_.map(s.sel, function (p) { return self.byPath(p); }));
      this.syncFonts();
      this.applyZoom();
      this.refresh({ tree: true });
      this.updateNote();
    },

    undo: function () {
      if (!this.undoStack.length) { return; }
      this.redoStack.push(this.snapshot());
      this.restore(this.undoStack.pop());
    },

    redo: function () {
      if (!this.redoStack.length) { return; }
      this.undoStack.push(this.snapshot());
      this.restore(this.redoStack.pop());
    },

    // Checkpoint once per continuous interaction (colour picker drag, slider …)
    beginInteractive: function () {
      if (!this._iact) {
        this.checkpoint();
        this._iact = true;
      }
    },

    endInteractive: function () {
      this._iact = false;
      this.scheduleColors();
      this.scheduleTree();
    },

    /* ------------------------------------------------------------------ */
    /* Selection                                                            */
    /* ------------------------------------------------------------------ */

    editable: function () {
      var root = this.root;
      return _.filter(this.selection, function (el) {
        return el.parentNode && root && root.contains(el) && !isLocked(el);
      });
    },

    setSelection: function (els) {
      var root = this.root;
      this.closePop();
      this.selection = _.filter(els, function (el) { return el && el.parentNode && root.contains(el); });
      this.refresh({ tree: false });
    },

    hitTest: function (target, deep) {
      var node = target, found = null;
      if (!node || !this.root || node === this.root || !this.root.contains(node)) { return null; }
      if (!deep) {
        while (node && node !== this.root) {
          if (node.parentNode === this.level) { found = node; break; }
          node = node.parentNode;
        }
      } else {
        while (node && node !== this.root && !RENDER[node.localName]) { node = node.parentNode; }
        if (node && node !== this.root) { found = node; }
      }
      if (!found || !RENDER[found.localName]) { return null; }
      for (node = found; node && node !== this.root; node = node.parentNode) {
        if (isLocked(node)) { return null; }
      }
      return found;
    },

    containerOf: function (el) {
      var p = el.parentNode;
      while (p && p !== this.root && !CONTAINER[p.localName]) { p = p.parentNode; }
      return p || this.root;
    },

    enterGroup: function (g) {
      this.level = g;
      this.selection = [];
      this.refresh({ tree: false });
    },

    exitGroup: function () {
      var old = this.level;
      if (old === this.root) { return; }
      this.level = this.containerOf(old);
      this.selection = [old];
      this.refresh({ tree: false });
    },

    refresh: function (opts) {
      this.drawOverlay();
      this.updateUi();
      this.syncAppearance();
      this.syncText();
      this.syncGeometry();
      this.scheduleColors();
      if (opts && opts.tree) { this.scheduleTree(); } else { this.updateTreeSelection(); }
    },

    /* ------------------------------------------------------------------ */
    /* Overlay (selection box + handles)                                    */
    /* ------------------------------------------------------------------ */

    clientRects: function (els) {
      return _.filter(_.map(els, function (el) { return el.getBoundingClientRect(); }), function (r) { return !emptyRect(r); });
    },

    drawOverlay: function (quad) {
      var ov = this.overlay, hostRect, sel, rects, u, ox, oy, i, pos, h, cx;
      var names = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
      if (!ov) { return; }
      while (ov.firstChild) { ov.removeChild(ov.firstChild); }
      this.marqEl = null;
      sel = _.filter(this.selection, function (el) { return el.parentNode; });
      if (!sel.length || !this.root) { return; }
      hostRect = this.ui.docHost[0].getBoundingClientRect();
      ox = hostRect.left;
      oy = hostRect.top;
      rects = this.clientRects(sel);
      if (!rects.length) { return; }
      u = rectUnion(rects);

      if (quad) {
        ov.appendChild(svgNode('polygon', {
          points: _.map(quad, function (p) { return (p.x - ox) + ',' + (p.y - oy); }).join(' '),
          fill: 'none', stroke: TEAL, 'stroke-width': 1.5, 'stroke-dasharray': '5 3'
        }));
        return;
      }

      if (sel.length > 1) {
        _.each(rects, function (r) {
          ov.appendChild(svgNode('rect', {
            x: r.left - ox, y: r.top - oy, width: r.width, height: r.height,
            fill: 'none', stroke: TEAL, 'stroke-width': 1, opacity: 0.6
          }));
        });
      }
      ov.appendChild(svgNode('rect', {
        x: u.l - ox, y: u.t - oy, width: u.r - u.l, height: u.b - u.t,
        fill: 'none', stroke: TEAL, 'stroke-width': 1.5, 'stroke-dasharray': sel.length > 1 ? '5 3' : ''
      }));
      if (!this.editable().length) { return; }

      cx = (u.l + u.r) / 2 - ox;
      ov.appendChild(svgNode('line', { x1: cx, y1: u.t - oy, x2: cx, y2: u.t - oy - 22, stroke: TEAL, 'stroke-width': 1 }));
      h = svgNode('circle', {
        cx: cx, cy: u.t - oy - 22, r: 5.5, fill: '#fff', stroke: TEAL, 'stroke-width': 1.5,
        'data-h': 'rotate', style: 'pointer-events:all;cursor:grab;'
      });
      ov.appendChild(h);
      for (i = 0; i < names.length; i++) {
        pos = HANDLE_POS[names[i]];
        ov.appendChild(svgNode('rect', {
          x: u.l - ox + pos[0] * (u.r - u.l) - HANDLE / 2,
          y: u.t - oy + pos[1] * (u.b - u.t) - HANDLE / 2,
          width: HANDLE, height: HANDLE, fill: '#fff', stroke: TEAL, 'stroke-width': 1.5,
          'data-h': names[i], style: 'pointer-events:all;cursor:' + HANDLE_CURSOR[names[i]] + ';'
        }));
      }
    },

    drawMarquee: function (x0, y0, x1, y1) {
      var hostRect = this.ui.docHost[0].getBoundingClientRect();
      if (!this.marqEl) {
        this.marqEl = svgNode('rect', { fill: 'rgba(90,99,176,.12)', stroke: TEAL, 'stroke-width': 1, 'stroke-dasharray': '4 3' });
        this.overlay.appendChild(this.marqEl);
      }
      this.marqEl.setAttribute('x', Math.min(x0, x1) - hostRect.left);
      this.marqEl.setAttribute('y', Math.min(y0, y1) - hostRect.top);
      this.marqEl.setAttribute('width', Math.abs(x1 - x0));
      this.marqEl.setAttribute('height', Math.abs(y1 - y0));
    },

    /* ------------------------------------------------------------------ */
    /* Mouse interaction                                                    */
    /* ------------------------------------------------------------------ */

    makeItems: function (els) {
      return _.map(els, function (el) {
        var P = screenM(el.parentNode);
        return { el: el, T0: getM(el), P: P, Pinv: safeInverse(P) };
      });
    },

    applyScreenMatrix: function (items, Ms) {
      _.each(items, function (it) {
        setM(it.el, it.Pinv.multiply(Ms).multiply(it.P).multiply(it.T0));
      });
    },

    onStageMouseDown: function (ev) {
      var e = ev.originalEvent || ev;
      var stage = this.ui.stage[0];
      var path, tgt, h, hit, deep, sel;
      if (e.button === 2 || !this.root) { return; }
      if ($(e.target).closest('.ve-zoom-controls,.ve-breadcrumb,.ve-bg-btn,.ve-unit-toggle,.ve-error').length) { return; }
      if (e.target === stage && (ev.offsetX > stage.clientWidth || ev.offsetY > stage.clientHeight)) { return; }
      this.hideCtx();
      path = e.composedPath ? e.composedPath() : [e.target];
      tgt = path[0];

      if (e.button === 1 || this._space) {
        this.drag = { type: 'pan', x: e.clientX, y: e.clientY, sl: stage.scrollLeft, st: stage.scrollTop };
        e.preventDefault();
        return;
      }

      h = tgt && tgt.getAttribute && tgt.getAttribute('data-h');
      if (h) {
        this.startHandleDrag(h, e);
        e.preventDefault();
        return;
      }

      if (this.insertTool) {
        this.drag = { type: 'draw', tool: this.insertTool, x: e.clientX, y: e.clientY, moved: false };
        e.preventDefault();
        return;
      }

      deep = e.metaKey || e.ctrlKey;
      hit = this.hitTest(tgt, deep);
      if (hit) {
        if (deep) { this.level = this.containerOf(hit); }
        if (e.shiftKey) {
          if (_.contains(this.selection, hit)) {
            this.setSelection(_.without(this.selection, hit));
            e.preventDefault();
            return;
          }
          this.setSelection(this.selection.concat([hit]));
        } else if (!_.contains(this.selection, hit)) {
          this.setSelection([hit]);
        }
        sel = this.editable();
        if (sel.length) {
          this.drag = {
            type: 'move', x: e.clientX, y: e.clientY, moved: false,
            items: this.makeItems(sel), snap: null
          };
        }
      } else {
        this.drag = { type: 'marquee', x: e.clientX, y: e.clientY, moved: false, additive: e.shiftKey };
      }
      e.preventDefault();
    },

    startHandleDrag: function (h, e) {
      var sel = this.editable(), rects = this.clientRects(sel), u;
      if (!sel.length || !rects.length) { return; }
      u = rectUnion(rects);
      this.drag = {
        type: h === 'rotate' ? 'rotate' : 'scale', handle: h, x: e.clientX, y: e.clientY, moved: false,
        items: this.makeItems(sel), box: u,
        cx: (u.l + u.r) / 2, cy: (u.t + u.b) / 2
      };
    },

    onDocMouseMove: function (ev) {
      var d = this.drag, dx, dy, Ms, stage = this.ui.stage[0], box, pos, hx, hy, ax, ay, sx, sy, s, a0, a1, ang;
      if (!d) { return; }
      dx = ev.clientX - d.x;
      dy = ev.clientY - d.y;
      if (d.type === 'pan') {
        stage.scrollLeft = d.sl - dx;
        stage.scrollTop = d.st - dy;
        return;
      }
      if (!d.moved && Math.abs(dx) < 3 && Math.abs(dy) < 3) { return; }
      if (!d.moved) {
        d.moved = true;
        if (d.type !== 'marquee' && d.type !== 'draw') { this.checkpoint(); }
      }

      if (d.type === 'move') {
        if (ev.shiftKey) {
          if (Math.abs(dx) > Math.abs(dy)) { dy = 0; } else { dx = 0; }
        }
        this.applyScreenMatrix(d.items, mTranslate(dx, dy));
        this.drawOverlay();
      } else if (d.type === 'draw') {
        if (d.tool !== 'text') { this.drawPreview(d, ev); }
      } else if (d.type === 'marquee') {
        this.drawMarquee(d.x, d.y, ev.clientX, ev.clientY);
      } else if (d.type === 'scale') {
        box = d.box;
        pos = HANDLE_POS[d.handle];
        hx = box.l + pos[0] * (box.r - box.l) + dx;
        hy = box.t + pos[1] * (box.b - box.t) + dy;
        if (ev.altKey) {
          ax = d.cx;
          ay = d.cy;
        } else {
          ax = box.l + (1 - pos[0]) * (box.r - box.l);
          ay = box.t + (1 - pos[1]) * (box.b - box.t);
        }
        sx = pos[0] === 0.5 ? 1 : (hx - ax) / ((box.l + pos[0] * (box.r - box.l)) - ax || 1);
        sy = pos[1] === 0.5 ? 1 : (hy - ay) / ((box.t + pos[1] * (box.b - box.t)) - ay || 1);
        if (ev.shiftKey) {
          if (pos[0] === 0.5) {
            sx = sy;
          } else if (pos[1] === 0.5) {
            sy = sx;
          } else {
            s = Math.abs(sx - 1) > Math.abs(sy - 1) ? sx : sy;
            sx = s;
            sy = s;
          }
        }
        sx = Math.abs(sx) < 0.01 ? (sx < 0 ? -0.01 : 0.01) : sx;
        sy = Math.abs(sy) < 0.01 ? (sy < 0 ? -0.01 : 0.01) : sy;
        Ms = mTranslate(ax, ay).multiply(mScale(sx, sy)).multiply(mTranslate(-ax, -ay));
        this.applyScreenMatrix(d.items, Ms);
        this.drawOverlay(this.quadFor(box, Ms));
      } else if (d.type === 'rotate') {
        a0 = Math.atan2(d.y - d.cy, d.x - d.cx);
        a1 = Math.atan2(ev.clientY - d.cy, ev.clientX - d.cx);
        ang = a1 - a0;
        if (ev.shiftKey) { ang = Math.round(ang / (Math.PI / 12)) * (Math.PI / 12); }
        Ms = mTranslate(d.cx, d.cy).multiply(mRotate(ang)).multiply(mTranslate(-d.cx, -d.cy));
        this.applyScreenMatrix(d.items, Ms);
        this.drawOverlay(this.quadFor(d.box, Ms));
      }
    },

    quadFor: function (box, Ms) {
      var pts = [[box.l, box.t], [box.r, box.t], [box.r, box.b], [box.l, box.b]];
      return _.map(pts, function (p) {
        var q = Ms.transformPoint({ x: p[0], y: p[1] });
        return { x: q.x, y: q.y };
      });
    },

    onDocMouseUp: function (ev) {
      var d = this.drag, sel, r, hits, self = this, mx0, my0, mx1, my1;
      if (!d) { return; }
      this.drag = null;
      if (d.type === 'pan') { return; }
      if (d.type === 'draw') {
        this.finishDraw(d, ev);
        return;
      }
      if (d.type === 'marquee') {
        if (!d.moved) {
          if (!d.additive) { this.setSelection([]); }
          return;
        }
        mx0 = Math.min(d.x, ev.clientX);
        my0 = Math.min(d.y, ev.clientY);
        mx1 = Math.max(d.x, ev.clientX);
        my1 = Math.max(d.y, ev.clientY);
        hits = _.filter(renderableKids(this.level), function (el) {
          if (isLocked(el) || isHidden(el)) { return false; }
          r = el.getBoundingClientRect();
          return !emptyRect(r) && r.right >= mx0 && r.left <= mx1 && r.bottom >= my0 && r.top <= my1;
        });
        this.setSelection(d.additive ? _.union(this.selection, hits) : hits);
        return;
      }
      if (d.moved) {
        sel = this.selection;
        self.refresh({ tree: false });
        this.selection = sel;
      }
    },

    onStageDblClick: function (ev) {
      var e = ev.originalEvent || ev;
      var tgt = e.composedPath ? e.composedPath()[0] : e.target;
      var hit;
      if (!this.root || $(e.target).closest('.ve-zoom-controls,.ve-breadcrumb,.ve-bg-btn,.ve-unit-toggle').length) { return; }
      hit = this.hitTest(tgt, false);
      if (hit && hit.localName === 'text') {
        this.setSelection([hit]);
        this.ui.textContent.focus().select();
      } else if (hit && hit.localName === 'image') {
        this.setSelection([hit]);
        this.editImage(hit);
      } else if (hit && CONTAINER[hit.localName]) {
        this.enterGroup(hit);
      } else if (!hit && this.level !== this.root) {
        this.exitGroup();
      }
    },

    onStageContext: function (ev) {
      var e = ev.originalEvent || ev;
      var tgt = e.composedPath ? e.composedPath()[0] : e.target;
      var hit;
      if (!this.root) { return; }
      e.preventDefault();
      if ($(e.target).closest('.ve-zoom-controls,.ve-breadcrumb,.ve-bg-btn,.ve-unit-toggle').length) { return; }
      hit = this.hitTest(tgt, e.metaKey || e.ctrlKey);
      if (hit && !_.contains(this.selection, hit)) {
        if (e.metaKey || e.ctrlKey) { this.level = this.containerOf(hit); }
        this.setSelection([hit]);
      }
      this.showCtx(e.clientX, e.clientY);
    },

    /* ------------------------------------------------------------------ */
    /* Keyboard                                                             */
    /* ------------------------------------------------------------------ */

    onKeyDown: function (e) {
      var tag = (e.target && e.target.tagName) || '', mod = e.ctrlKey || e.metaKey, k = e.key, step;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || this.isBlocked()) { return; }
      if (!this.root) { return; }
      if (k === ' ' || e.code === 'Space') {
        this._space = true;
        this.ui.stage.css('cursor', 'grab');
        e.preventDefault();
        return;
      }
      if (mod && (k === 'z' || k === 'Z')) {
        e.preventDefault();
        if (e.shiftKey) { this.redo(); } else { this.undo(); }
      } else if (mod && (k === 'y' || k === 'Y')) {
        e.preventDefault();
        this.redo();
      } else if (mod && (k === 'd' || k === 'D')) {
        e.preventDefault();
        this.duplicateSel();
      } else if (mod && (k === 'g' || k === 'G')) {
        e.preventDefault();
        if (e.shiftKey) { this.ungroupSel(); } else { this.groupSel(); }
      } else if (mod && e.code === 'Digit7') {
        e.preventDefault();
        if (e.altKey) { this.releaseClip(); } else { this.makeClip(); }
      } else if (mod && (k === 'a' || k === 'A')) {
        e.preventDefault();
        this.selectAll();
      } else if (mod && (k === ']' || k === '}')) {
        e.preventDefault();
        this.arrange(e.shiftKey ? 'to-front' : 'forward');
      } else if (mod && (k === '[' || k === '{')) {
        e.preventDefault();
        this.arrange(e.shiftKey ? 'to-back' : 'backward');
      } else if (k === 'Delete' || k === 'Backspace') {
        e.preventDefault();
        this.deleteSel();
      } else if (k === 'Escape') {
        if (this._pop) {
          this.closePop();
        } else if (this.insertTool) {
          this.setInsertTool(null);
        } else if (this.ui.ctx.css('display') !== 'none') {
          this.hideCtx();
        } else if (this.level !== this.root) {
          this.exitGroup();
        } else {
          this.setSelection([]);
        }
      } else if (/^Arrow(Left|Right|Up|Down)$/.test(k) && this.editable().length) {
        e.preventDefault();
        step = e.shiftKey ? 10 : 1;
        this.nudge(k === 'ArrowLeft' ? -step : (k === 'ArrowRight' ? step : 0), k === 'ArrowUp' ? -step : (k === 'ArrowDown' ? step : 0));
      }
    },

    nudge: function (dx, dy) {
      var now = Date.now();
      if (!this._nudgeAt || now - this._nudgeAt > 600) { this.checkpoint(); }
      this._nudgeAt = now;
      this.applyScreenMatrix(this.makeItems(this.editable()), mTranslate(dx, dy));
      this.drawOverlay();
    },

    /* ------------------------------------------------------------------ */
    /* Toolbar actions                                                      */
    /* ------------------------------------------------------------------ */

    onAction: function (e) {
      var $b = $(e.currentTarget), a = $b.attr('data-action');
      if ($b.hasClass('ve-disabled')) { return; }
      this.runAction(a);
    },

    runAction: function (a) {
      switch (a) {
        case 'delete': this.deleteSel(); break;
        case 'duplicate': this.duplicateSel(); break;
        case 'group': this.groupSel(); break;
        case 'ungroup': this.ungroupSel(); break;
        case 'ungroup-all': this.ungroupAllSel(); break;
        case 'to-front':
        case 'forward':
        case 'backward':
        case 'to-back': this.arrange(a); break;
        case 'rotate-left':
        case 'rotate-right':
        case 'flip-h':
        case 'flip-v': this.transformSel(a); break;
        case 'select-all': this.selectAll(); break;
        case 'trim': this.trim(); break;
        case 'undo': this.undo(); break;
        case 'redo': this.redo(); break;
        case 'exit-group': this.exitGroup(); break;
        case 'toggle-bg': this.toggleBg(); break;
        case 'zoom-in': this.setZoom(this.zoom * 1.25); break;
        case 'zoom-out': this.setZoom(this.zoom / 1.25); break;
        case 'zoom-fit': this.zoomFit(); break;
        case 'cancel': this.cancel(); break;
        case 'text-outline': this.outlineText(); break;
        case 'pop-close': this.closePop(); break;
        case 'clip-make': this.makeClip(); break;
        case 'clip-release': this.releaseClip(); break;
        case 'unit-mm': this.setUnit('mm'); break;
        case 'unit-px': this.setUnit('px'); break;
        case 'cut': this.copySel(true, null); break;
        case 'copy': this.copySel(false, null); break;
        case 'paste': this.pasteButton(); break;
        case 'insert-text': this.setInsertTool('text'); break;
        case 'insert-rect': this.setInsertTool('rect'); break;
        case 'insert-ellipse': this.setInsertTool('ellipse'); break;
        case 'edit-image': this.editImage(); break;
        case 'center-h':
        case 'center-v':
        case 'center-both':
        case 'fit-artboard': this.alignSel(a); break;
        case 'artboard-fit': this.fitArtboardToArtwork(); break;
        case 'tool-artboard': this.setTool('artboard'); break;
        case 'artboard-apply': this.applyArtboard(); break;
        case 'artboard-swap': this.swapArtboard(); break;
        case 'tool-replace': this.setTool('replace'); break;
        case 'tool-reduce': this.setTool('reduce'); break;
        case 'tool-close': this.setTool(null); break;
        case 'replace-apply': this.replaceApply(); break;
        case 'save-svg': this.saveSvg(); break;
        case 'save-pdf': this.savePdf(); break;
      }
    },

    updateUi: function () {
      var sel = this.editable(), has = sel.length > 0;
      var hasGroup = _.some(sel, function (el) { return el.localName === 'g'; });
      var self = this;
      function dis(action, flag) {
        self.$('[data-action="' + action + '"]').toggleClass('ve-disabled', !!flag);
      }
      _.each(['delete', 'duplicate', 'group', 'to-front', 'forward', 'backward', 'to-back',
        'rotate-left', 'rotate-right', 'flip-h', 'flip-v'], function (a) { dis(a, !has); });
      dis('ungroup', !hasGroup);
      dis('ungroup-all', !this.root || !this.root.querySelector('g'));
      dis('undo', !this.undoStack.length);
      dis('redo', !this.redoStack.length);
      dis('select-all', !this.root);
      dis('trim', !this.root);
      dis('cut', !has);
      _.each(['center-h', 'center-v', 'center-both', 'fit-artboard'], function (a) { dis(a, !has); });
      dis('text-outline', !this.hasText());
      dis('clip-make', !this.canMakeClip());
      dis('clip-release', !this.canReleaseClip());
      dis('copy', !has);
      dis('edit-image', !this.imagesInSel().length);
      this.ui.selInfo.text(this.selectionInfo());
      if (this.level && this.level !== this.root) {
        this.ui.breadcrumb.css('display', 'flex');
        this.ui.crumbText.text(t('vectorEditor.insideGroup', 'Inside:') + ' ' + typeLabel(this.level));
      } else {
        this.ui.breadcrumb.css('display', 'none');
      }
    },

    selectionInfo: function () {
      var sel = this.selection, box;
      if (!sel.length) { return t('vectorEditor.nothingSelected', 'Nothing selected'); }
      box = this.docBox(sel);
      return (sel.length > 1 ? sel.length + ' ' + t('vectorEditor.objectsSelected', 'objects selected') : typeLabel(sel[0])) +
        (box ? ' · ' + this.fmtSize(box.w, box.h) + ' (' + this.fmtSize(box.w, box.h, this.unit === 'mm' ? 'px' : 'mm') + ')' : '') +
        (sel.length === 1 && isLocked(sel[0]) ? ' · ' + t('vectorEditor.locked', 'locked') : '');
    },

    selectAll: function () {
      this.setSelection(_.filter(renderableKids(this.level), function (el) { return !isLocked(el) && !isHidden(el); }));
    },

    deleteSel: function () {
      var sel = this.editable(), self = this;
      if (!sel.length) { return; }
      this.checkpoint();
      _.each(sel, function (el) {
        if (el.parentNode) { el.parentNode.removeChild(el); }
      });
      if (!this.root.contains(this.level)) { this.level = this.root; }
      self.selection = [];
      this.refresh({ tree: true });
      this.updateNote();
    },

    uniquifyIds: function (clone) {
      var all = [clone].concat(Array.prototype.slice.call(clone.querySelectorAll('*')));
      var map = {}, any = false, i, j, el, attr, v;
      this._idCounter = (this._idCounter || 0) + 1;
      for (i = 0; i < all.length; i++) {
        if (all[i].getAttribute('id')) {
          map[all[i].getAttribute('id')] = all[i].getAttribute('id') + '-c' + this._idCounter;
          any = true;
        }
      }
      if (!any) { return; }
      function rewrite(str) {
        return str.replace(/url\(\s*(['"]?)#([^)'"\s]+)\1\s*\)/g, function (m, q, id) {
          return map[id] ? 'url(' + q + '#' + map[id] + q + ')' : m;
        });
      }
      for (i = 0; i < all.length; i++) {
        el = all[i];
        if (el.getAttribute('id')) { el.setAttribute('id', map[el.getAttribute('id')]); }
        for (j = 0; j < el.attributes.length; j++) {
          attr = el.attributes[j];
          v = attr.value;
          if (attr.localName === 'href' && v.charAt(0) === '#' && map[v.slice(1)]) {
            attr.value = '#' + map[v.slice(1)];
          } else if (v.indexOf('url(') !== -1) {
            attr.value = rewrite(v);
          }
        }
      }
    },

    duplicateSel: function () {
      var sel = docOrder(this.editable()), clones = [], self = this, items;
      if (!sel.length) { return; }
      this.checkpoint();
      _.each(sel, function (el) {
        var c = el.cloneNode(true);
        self.uniquifyIds(c);
        el.parentNode.insertBefore(c, el.nextSibling);
        clones.push(c);
      });
      items = this.makeItems(clones);
      this.applyScreenMatrix(items, mTranslate(14, 14));
      this.selection = clones;
      this.refresh({ tree: true });
      this.updateNote();
    },

    groupSel: function () {
      var sel = this.editable(), parent, g;
      if (!sel.length) { return; }
      parent = sel[0].parentNode;
      if (!_.every(sel, function (el) { return el.parentNode === parent; })) {
        this.notify(t('vectorEditor.groupSameLevel', 'Select objects on the same level to group them.'));
        return;
      }
      this.checkpoint();
      sel = docOrder(sel);
      g = svgNode('g');
      parent.insertBefore(g, _.last(sel).nextSibling);
      _.each(sel, function (el) { g.appendChild(el); });
      this.selection = [g];
      this.refresh({ tree: true });
    },

    readStyles: function (el) {
      var cs = window.getComputedStyle(el), out = {};
      _.each(INHERIT, function (p) { out[p] = cs.getPropertyValue(p); });
      return out;
    },

    ungroupOne: function (g) {
      var self = this, parent = g.parentNode, children = kids(g), before = [], Tg, go, dropped = false, out = [];
      var sty = g.style;
      go = parseFloat(sty.opacity || g.getAttribute('opacity') || '1');
      if (isNaN(go)) { go = 1; }
      dropped = hasClipEffect(g);
      _.each(children, function (k) {
        before.push(RENDER[k.localName] ? self.readStyles(k) : null);
      });
      Tg = getM(g);
      _.each(children, function (k) {
        parent.insertBefore(k, g);
      });
      parent.removeChild(g);
      _.each(children, function (k, i) {
        var after, op;
        if (!Tg.isIdentity && k.transform) { setM(k, Tg.multiply(getM(k))); }
        if (before[i]) {
          after = self.readStyles(k);
          _.each(INHERIT, function (p) {
            if (before[i][p] !== after[p] && before[i][p]) { k.style.setProperty(p, before[i][p]); }
          });
          if (go < 1) {
            op = parseFloat(k.style.opacity || k.getAttribute('opacity') || '1');
            k.style.setProperty('opacity', String(+(go * (isNaN(op) ? 1 : op)).toFixed(4)));
          }
          out.push(k);
        }
      });
      return { items: out, dropped: dropped };
    },

    ungroupSel: function () {
      var groups = _.filter(this.editable(), function (el) { return el.localName === 'g'; });
      var self = this, result = [], dropped = false;
      if (!groups.length) { return; }
      this.checkpoint();
      _.each(groups, function (g) {
        var r = self.ungroupOne(g);
        result = result.concat(r.items);
        dropped = dropped || r.dropped;
      });
      this.selection = result;
      this.refresh({ tree: true });
      if (dropped) {
        this.notify(t('vectorEditor.clipReleased', 'The ungrouped group had a clipping mask / filter, which was released.'));
      }
    },

    // Dissolves every group inside the selection (or the whole artwork when nothing
    // is selected), nested ones included. Clipped / masked / filtered groups are
    // kept so the artwork does not change its look — use "Ungroup" to release those.
    ungroupAllSel: function () {
      var self = this, sel = this.editable(), roots = sel.length ? sel : [this.root], groups = [], result = [], count = 0, kept = 0;
      _.each(roots, function (r) {
        if (r.localName === 'g') { groups.push(r); }
        groups = groups.concat(Array.prototype.slice.call(r.querySelectorAll('g')));
      });
      groups = _.filter(docOrder(_.uniq(groups)), function (g) { return !inNonRendered(g); });
      if (!groups.length) { return; }
      this.checkpoint();
      _.each(groups, function (g) {
        var r;
        if (!g.parentNode) { return; }
        if (hasClipEffect(g)) {
          kept++;
          return;
        }
        r = self.ungroupOne(g);
        count++;
        result = result.concat(r.items);
      });
      if (sel.length) {
        result = _.filter(_.union(_.filter(sel, function (el) { return el.localName !== 'g'; }), result),
          function (el) { return el.parentNode && el.localName !== 'g'; });
      } else {
        result = [];
      }
      if (!this.root.contains(this.level)) { this.level = this.root; }
      this.selection = result;
      this.refresh({ tree: true });
      this.updateNote();
      this.notify(count + ' ' + t('vectorEditor.groupsUngrouped', 'groups ungrouped') +
        (kept ? ' · ' + kept + ' ' + t('vectorEditor.clippedKept', 'groups with a clip / mask / filter were kept (use Ungroup to release them)') : ''));
    },

    arrange: function (how) {
      var sel = this.editable(), asc, desc, self = this, changed = false;
      if (!sel.length) { return; }
      this.checkpoint();
      asc = docOrder(sel);
      desc = asc.slice().reverse();
      if (how === 'to-front') {
        _.each(asc, function (el) { el.parentNode.appendChild(el); });
      } else if (how === 'to-back') {
        _.each(desc, function (el) { el.parentNode.insertBefore(el, el.parentNode.firstChild); });
      } else if (how === 'forward') {
        _.each(desc, function (el) {
          var n = el.nextElementSibling;
          while (n && !RENDER[n.localName] && n.nextElementSibling) { n = n.nextElementSibling; }
          if (n && !_.contains(sel, n)) {
            el.parentNode.insertBefore(n, el);
            changed = true;
          }
        });
      } else if (how === 'backward') {
        _.each(asc, function (el) {
          var p = el.previousElementSibling;
          while (p && !RENDER[p.localName] && p.previousElementSibling) { p = p.previousElementSibling; }
          if (p && !_.contains(sel, p)) {
            el.parentNode.insertBefore(el, p);
            changed = true;
          }
        });
      }
      self.refresh({ tree: true });
      return changed;
    },

    transformSel: function (kind) {
      var sel = this.editable(), rects, u, cx, cy, Ms, items;
      if (!sel.length) { return; }
      rects = this.clientRects(sel);
      if (!rects.length) { return; }
      u = rectUnion(rects);
      cx = (u.l + u.r) / 2;
      cy = (u.t + u.b) / 2;
      switch (kind) {
        case 'flip-h': Ms = mScale(-1, 1); break;
        case 'flip-v': Ms = mScale(1, -1); break;
        case 'rotate-left': Ms = mRotate(-Math.PI / 2); break;
        default: Ms = mRotate(Math.PI / 2);
      }
      Ms = mTranslate(cx, cy).multiply(Ms).multiply(mTranslate(-cx, -cy));
      this.checkpoint();
      items = this.makeItems(sel);
      this.applyScreenMatrix(items, Ms);
      this.drawOverlay();
    },

    toggleLock: function (els) {
      var all = _.every(els, isLocked), self = this;
      this.checkpoint();
      _.each(els, function (el) {
        if (all) { el.removeAttribute('data-ve-locked'); } else { el.setAttribute('data-ve-locked', '1'); }
      });
      self.refresh({ tree: true });
    },

    toggleHidden: function (el) {
      this.checkpoint();
      if (isHidden(el)) {
        el.removeAttribute('data-ve-hidden');
        el.removeAttribute('display');
      } else {
        el.setAttribute('data-ve-hidden', '1');
        el.setAttribute('display', 'none');
      }
      this.refresh({ tree: true });
    },

    trim: function (extra) {
      var rects = [], maxStroke = 0, u, inv, pts, xs, ys, pad, count = 0, self = this;
      _.each(renderableKids(this.root), function (el) {
        var r;
        if (isHidden(el)) { return; }
        r = el.getBoundingClientRect();
        if (!emptyRect(r)) { rects.push(r); }
      });
      if (!rects.length) { return; }
      _.each(shapesIn(this.root), function (el) {
        var sw, cs;
        if (count++ > 2000) { return; }
        cs = window.getComputedStyle(el);
        if (cs.getPropertyValue('stroke') !== 'none') {
          sw = parseFloat(cs.getPropertyValue('stroke-width')) || 0;
          maxStroke = Math.max(maxStroke, sw);
        }
      });
      u = rectUnion(rects);
      inv = safeInverse(screenM(this.root));
      pts = _.map([[u.l, u.t], [u.r, u.t], [u.r, u.b], [u.l, u.b]], function (p) {
        return inv.transformPoint({ x: p[0], y: p[1] });
      });
      xs = _.pluck(pts, 'x');
      ys = _.pluck(pts, 'y');
      pad = maxStroke / 2 + (extra > 0 ? extra : 0);
      this.checkpoint();
      this.vb = {
        x: Math.min.apply(null, xs) - pad, y: Math.min.apply(null, ys) - pad,
        w: Math.max.apply(null, xs) - Math.min.apply(null, xs) + pad * 2,
        h: Math.max.apply(null, ys) - Math.min.apply(null, ys) + pad * 2
      };
      this.vbDirty = true;
      self.zoomFit();
      self.updateNote();
      if (self.tool === 'artboard') { self.renderArtboard(); }
    },

    // Artboard = artwork bounds (+ margin from the Artboard panel).
    fitArtboardToArtwork: function () {
      var m = parseFloat(this.ui.abMargin.val());
      this.trim(isNaN(m) || m < 0 ? 0 : this.fromUnit(m));
    },

    /* ------------------------------------------------------------------ */
    /* Appearance                                                           */
    /* ------------------------------------------------------------------ */

    /* ------------------------------------------------------------------ */
    /* Colour popup (Fill / Stroke)                                         */
    /* ------------------------------------------------------------------ */

    paletteHtml: function (colors, titleFn) {
      return _.map(colors, function (c) {
        return '<button type="button" class="ve-pop-sw" data-hex="' + c + '" title="' + _.escape(titleFn ? titleFn(c) : c) + '" style="background:' + c + '"></button>';
      }).join('');
    },

    onFillClick: function (e) {
      e.preventDefault();
      this.openColorPop('fill', e.currentTarget);
    },

    onStrokeClick: function (e) {
      e.preventDefault();
      this.openColorPop('stroke', e.currentTarget);
    },

    setPopColor: function (hex) {
      var c = rgbToCmyk(hex);
      this.ui.popColor.val(hex);
      this.ui.popHex.val(hex.toUpperCase());
      this.ui.pop.find('.ve-pop-cmyk').each(function () {
        this.value = c[{ c: 0, m: 1, y: 2, k: 3 }[this.getAttribute('data-ch')]];
      });
    },

    openColorPop: function (prop, anchor) {
      var shapes = this.selectionShapes(), cs, v, hex;
      if (!shapes.length) { return; }
      cs = window.getComputedStyle(shapes[0]);
      v = cs.getPropertyValue(prop);
      hex = toHex(v) || (/^url\(/.test(v) ? this.firstStopHex(v) : null);
      this._pop = { mode: 'paint', prop: prop, from: toHex(v), refs: null, dirty: false };
      this.ui.popTitle.text(prop === 'fill' ? t('vectorEditor.fill', 'Fill') : t('vectorEditor.stroke', 'Stroke'));
      this.finishOpenPop(anchor, hex || '#000000', v === 'none');
    },

    // Click on a swatch in the Colors panel: same popup, but it recolours that colour's uses.
    openRowPop: function (idx, anchor, $row) {
      var entry = this._colorEntries[idx];
      if (!entry) { return; }
      this._pop = { mode: 'row', prop: null, from: entry.hex, entry: entry, $row: $row, refs: null, dirty: false };
      this.ui.popTitle.text(t('vectorEditor.color', 'Color') + ' ' + entry.hex.toUpperCase());
      this.finishOpenPop(anchor, entry.hex, false);
    },

    finishOpenPop: function (anchor, hex, isNone) {
      var pop = this.ui.pop, used, r, pr, w, h, left, top;
      this.setPopColor(hex);
      this.ui.popNone.toggleClass('ve-active', isNone);
      this.ui.popAll.prop('checked', false).prop('disabled', !this._pop.from);
      this.ui.popTolRow.css('display', 'none');
      this.ui.popTol.val(0);
      this.ui.popTolVal.text('0%');
      if (!this.ui.popBase.children().length) { this.ui.popBase.html(this.paletteHtml(BASE_COLORS)); }
      used = this.collectColors(true).entries;
      this.ui.popUsed.html(this.paletteHtml(_.pluck(used, 'hex'), function (c) { return c + ' · ' + cmykLabel(c); }));
      pop.css({ display: 'flex', left: 0, top: 0 });
      pr = (pop[0].offsetParent || this.el).getBoundingClientRect();
      r = anchor.getBoundingClientRect();
      w = pop.outerWidth();
      h = pop.outerHeight();
      left = Math.max(8, Math.min(r.right - pr.left - w, pr.width - w - 8));
      top = Math.max(8, Math.min(r.bottom - pr.top + 6, pr.height - h - 8));
      pop.css({ left: left, top: top });
    },

    onColorRowClick: function (e) {
      var $row = $(e.currentTarget).closest('.ve-color-row');
      e.preventDefault();
      this.openRowPop(parseInt($row.attr('data-idx'), 10), e.currentTarget, $row);
    },

    closePop: function () {
      if (!this._pop) { return; }
      this._pop = null;
      this.ui.pop.css('display', 'none');
      this._iact = false;
      this.scheduleColors();
      this.scheduleTree();
    },

    // Same colours as the Replace tool: either just the selection, or every use of the colour.
    popRefs: function () {
      var from = this._pop.from, tol = (parseInt(this.ui.popTol.val(), 10) || 0) / 100 * 441.67, fromRgb = hexRgb(from), refs = [];
      _.each(this.collectColors(true).entries, function (e) {
        if (e.hex === from || Math.sqrt(rgbDist2(hexRgb(e.hex), fromRgb)) <= tol) { refs = refs.concat(e.refs); }
      });
      return refs;
    },

    applyPop: function (value) {
      var pop = this._pop, none = value === 'none', refs = [], seen = [];
      if (!pop) { return; }
      if (!pop.dirty) {
        this.checkpoint();
        pop.dirty = true;
      }
      if (pop.mode === 'row') {
        refs = pop.entry.refs;
      } else {
        this.setPaint(pop.prop, value);
      }
      if (this.ui.popAll.prop('checked') && pop.from) {
        if (!pop.refs) { pop.refs = this.popRefs(); }
        refs = refs.concat(pop.refs);
      }
      _.each(refs, function (r) {
        var key = r.prop;
        if (_.some(seen, function (x) { return x.el === r.el && x.prop === key; })) { return; }
        seen.push({ el: r.el, prop: key });
        if (none && r.prop === 'stop-color') {
          r.el.style.setProperty('stop-opacity', '0');
        } else {
          r.el.style.setProperty(r.prop, value);
        }
      });
      if (pop.mode === 'paint' && pop.prop === 'stroke' && !none) {
        _.each(this.selectionShapes(), function (el) {
          var sw = parseFloat(window.getComputedStyle(el).getPropertyValue('stroke-width'));
          if (isNaN(sw) || sw <= 0) { el.style.setProperty('stroke-width', '1'); }
        });
      }
      this.ui.popNone.toggleClass('ve-active', none);
      if (!none) { this.setPopColor(value); }
      if (pop.mode === 'row' && pop.$row) {
        pop.entry.hex = none ? pop.entry.hex : value;
        if (!none) {
          pop.$row.find('input[type="color"]').val(value);
          pop.$row.find('.ve-color-hex').text(value);
          pop.$row.find('.ve-color-cmyk').text(cmykLabel(value));
        }
      }
      this.syncAppearance();
      this.drawOverlay();
    },

    onPopColor: function () { this.applyPop(this.ui.popColor.val().toLowerCase()); },

    onPopHex: function () {
      var v = this.ui.popHex.val();
      if (v.charAt(0) !== '#') { v = '#' + v; }
      if (/^#[0-9a-f]{6}$/i.test(v)) { this.applyPop(v.toLowerCase()); }
    },

    onPopCmyk: function () {
      var v = {};
      this.ui.pop.find('.ve-pop-cmyk').each(function () { v[this.getAttribute('data-ch')] = this.value; });
      this.applyPop(cmykToHex(v.c, v.m, v.y, v.k));
    },

    onPopNone: function () { this.applyPop('none'); },
    onPopSwatch: function (e) { this.applyPop($(e.currentTarget).attr('data-hex')); },

    onPopAll: function () {
      if (!this._pop) { return; }
      this._pop.refs = null;
      this.ui.popTolRow.css('display', this.ui.popAll.prop('checked') ? '' : 'none');
    },

    onPopTol: function () {
      if (!this._pop) { return; }
      this._pop.refs = null;
      this.ui.popTolVal.text(this.ui.popTol.val() + '%');
    },

    /* ------------------------------------------------------------------ */
    /* Convert text to outlines                                             */
    /* ------------------------------------------------------------------ */

    hasText: function () {
      return !!this.root && _.some(this.root.querySelectorAll('text'), function (tx) { return tx.textContent.length > 0; });
    },

    // Replaces text that uses an embedded font (e.g. text from a PDF) with real outlines.
    // Text in ordinary fonts has no font file we could read, so it stays text.
    outlineText: function () {
      var self = this, sel = this.editable(), roots = sel.length ? sel : [this.root], texts = [], css = this.fontStyle ? this.fontStyle.textContent : '';
      _.each(roots, function (r) {
        if (r.localName === 'text') { texts.push(r); }
        texts = texts.concat(Array.prototype.slice.call(r.querySelectorAll('text')));
      });
      texts = _.filter(_.uniq(texts), function (tx) { return !inNonRendered(tx) && tx.textContent.length > 0; });
      if (!texts.length) {
        this.notify(t('vectorEditor.outlineNoText', 'There is no text to convert.'));
        return;
      }
      if (!/@font-face/.test(css)) {
        this.notify(t('vectorEditor.outlineNoFonts', 'Only text that uses an embedded font (for example text from a PDF) can be converted — there is no font file for the other fonts.'));
        return;
      }
      loadGlobalScripts(window.opentype ? [] : [OPENTYPE_URL]).then(function () {
        var fonts = parseEmbeddedFonts(css), res, left;
        // freeze what the style sheet / editor settings currently make of every text, then outline it
        _.each(texts, function (tx) {
          _.each(textNodes(tx), function (n) {
            var cs = window.getComputedStyle(n);
            _.each(['fill', 'fill-opacity', 'stroke', 'stroke-width', 'stroke-opacity'], function (p) { n.setAttribute(p, cs.getPropertyValue(p)); });
            n.setAttribute('font-family', firstFamily(cs.getPropertyValue('font-family')));
            n.setAttribute('font-size', cs.getPropertyValue('font-size'));
          });
        });
        self.checkpoint();
        res = outlineEmbeddedText(texts, fonts);
        if (!res.done) {
          self.undoStack.pop();
          self.updateUi();
          self.notify(t('vectorEditor.outlineNoFonts', 'Only text that uses an embedded font (for example text from a PDF) can be converted — there is no font file for the other fonts.'));
          return;
        }
        left = _.filter(texts, function (tx) { return tx.parentNode && tx.textContent.length > 0; }).length;
        self.selection = sel.length ? res.groups : [];
        self.refresh({ tree: true });
        self.updateNote();
        self.notify(res.groups.length + ' ' + t('vectorEditor.outlined', 'text objects converted to outlines') +
          (left ? ' · ' + left + ' ' + t('vectorEditor.outlineLeft', 'left as text (no embedded font)') : ''));
      }, function () {
        self.notify(t('vectorEditor.outlineLib', 'The font reader could not be loaded.'));
      });
    },

    /* ------------------------------------------------------------------ */
    /* Clipping masks                                                       */
    /* ------------------------------------------------------------------ */

    clipRefOf: function (el) {
      var v = el.style.getPropertyValue('clip-path') || el.getAttribute('clip-path') || '', m = /url\(\s*["']?#([^)"'\s]+)/.exec(v);
      return m ? m[1] : null;
    },

    clipPathOf: function (el) {
      var id = this.clipRefOf(el), cp = id ? this.shadow.getElementById(id) : null;
      return cp && cp.localName === 'clipPath' ? cp : null;
    },

    canMakeClip: function () {
      var sel = this.editable();
      return sel.length >= 2 && _.every(sel, function (el) { return el.parentNode === sel[0].parentNode; });
    },

    canReleaseClip: function () {
      var self = this;
      return _.some(this.editable(), function (el) { return !!self.clipPathOf(el); });
    },

    ensureDefs: function () {
      var defs = _.find(kids(this.root), function (k) { return k.localName === 'defs'; });
      if (!defs) {
        defs = svgNode('defs');
        this.root.insertBefore(defs, this.root.firstChild);
      }
      return defs;
    },

    // The top-most selected object becomes the clip shape for all the others.
    makeClip: function () {
      var sel = docOrder(this.editable()), parent, top, rest, cp, g, id, i = 0, ref, Pinv, self = this;
      if (sel.length < 2) {
        this.notify(t('vectorEditor.clipNeedTwo', 'Select at least two objects: the top one becomes the clipping mask.'));
        return;
      }
      parent = sel[0].parentNode;
      if (!_.every(sel, function (el) { return el.parentNode === parent; })) {
        this.notify(t('vectorEditor.groupSameLevel', 'Select objects on the same level to group them.'));
        return;
      }
      top = _.last(sel);
      rest = _.initial(sel);
      Pinv = safeInverse(screenM(parent));
      cp = svgNode('clipPath');
      _.each(shapesIn(top), function (s) {
        var c, cs, names = [], j;
        if (!CLIP_OK[s.localName] || inNonRendered(s) || (s.parentNode && s.parentNode.localName === 'text')) { return; }
        cs = window.getComputedStyle(s);
        c = s.cloneNode(true);
        for (j = 0; j < c.attributes.length; j++) {
          if (/^(id|style|class|fill|fill-opacity|stroke.*|opacity|transform|clip-path|mask|filter|data-ve-.*)$/.test(c.attributes[j].name)) { names.push(c.attributes[j].name); }
        }
        _.each(names, function (n) { c.removeAttribute(n); });
        c.setAttribute('clip-rule', cs.getPropertyValue('fill-rule') === 'evenodd' ? 'evenodd' : 'nonzero');
        setM(c, Pinv.multiply(screenM(s)));
        cp.appendChild(c);
      });
      if (!cp.firstChild) {
        this.notify(t('vectorEditor.clipNoShape', 'The top object has no shape that can be used as a mask.'));
        return;
      }
      this.checkpoint();
      do {
        i++;
        id = 've-clip-' + i;
      } while (this.shadow.getElementById(id));
      cp.setAttribute('id', id);
      this.ensureDefs().appendChild(cp);
      ref = top.nextSibling;
      parent.removeChild(top);
      g = svgNode('g', { 'clip-path': 'url(#' + id + ')' });
      parent.insertBefore(g, ref);
      _.each(rest, function (el) { g.appendChild(el); });
      self.selection = [g];
      self.refresh({ tree: true });
      self.updateNote();
    },

    // Remove the mask; its shape stays behind as an object without fill or stroke.
    releaseClip: function () {
      var self = this, sel = _.filter(this.editable(), function (el) { return !!self.clipPathOf(el); }), made = [], ids = [];
      if (!sel.length) {
        this.notify(t('vectorEditor.clipNone', 'There is no clipping mask in the selection.'));
        return;
      }
      this.checkpoint();
      _.each(sel, function (el) {
        var cp = self.clipPathOf(el), parent = el.parentNode, ref = el.nextSibling, Tel = getM(el), bbM = new DOMMatrix(), cpM = getM(cp), bb;
        if (cp.getAttribute('clipPathUnits') === 'objectBoundingBox') {
          try {
            bb = el.getBBox();
            bbM = mTranslate(bb.x, bb.y).multiply(mScale(bb.width, bb.height));
          } catch (err) { /* element not measurable: keep identity */ }
        }
        _.each(kids(cp), function (c) {
          var clone;
          if (!CLIP_OK[c.localName]) { return; }
          clone = c.cloneNode(true);
          clone.removeAttribute('id');
          clone.removeAttribute('clip-path');
          setM(clone, Tel.multiply(bbM).multiply(cpM).multiply(getM(c)));
          clone.style.setProperty('fill', 'none');
          clone.style.setProperty('stroke', 'none');
          parent.insertBefore(clone, ref);
          made.push(clone);
        });
        ids.push(cp.getAttribute('id'));
        el.removeAttribute('clip-path');
        el.style.removeProperty('clip-path');
        if (!el.getAttribute('style')) { el.removeAttribute('style'); }
      });
      // drop mask definitions nothing refers to any more
      _.each(_.uniq(ids), function (id) {
        var cp = self.shadow.getElementById(id), xml;
        if (!cp) { return; }
        xml = new XMLSerializer().serializeToString(self.root);
        if (xml.indexOf('#' + id + ')') === -1 && xml.indexOf('#' + id + '"') === -1) { cp.parentNode.removeChild(cp); }
      });
      this.selection = sel.concat(made);
      this.refresh({ tree: true });
      this.updateNote();
      this.notify(t('vectorEditor.clipReleased2', 'Clipping mask released. The mask shape is now an object without fill or stroke (see the Objects panel).'));
    },

    /* ------------------------------------------------------------------ */
    /* Artboard size                                                        */
    /* ------------------------------------------------------------------ */

    lenOut: function (u) {
      var px = u * this.exportPxPerUnit, m = /(mm|cm|in|pt|pc)\s*$/i.exec(this.docW || ''), unit = m ? m[1].toLowerCase() : '';
      var div = { mm: 96 / 25.4, cm: 96 / 2.54, 'in': 96, pt: 96 / 72, pc: 16 }[unit];
      return unit ? fmt(px / div) + unit : fmt(px);
    },

    mmToField: function (mm) { return +(this.unit === 'mm' ? mm : mm * 96 / 25.4).toFixed(this.unit === 'mm' ? 2 : 1); },
    fieldToMm: function (v) { return this.unit === 'mm' ? v : v * 25.4 / 96; },

    renderArtboard: function () {
      var wMm = this.vb.w * this.mmPerUnit, hMm = this.vb.h * this.mmPerUnit, key = String(Math.round(wMm * 10) / 10) + 'x' + String(Math.round(hMm * 10) / 10), match = '';
      this.ui.abW.val(this.mmToField(wMm));
      this.ui.abH.val(this.mmToField(hMm));
      this.ui.abUnit.text('(' + this.unit + ')');
      this.ui.abPreset.find('option').each(function () {
        if (this.value === key) { match = key; }
      });
      this.ui.abPreset.val(match);
      this._abRatio = hMm / wMm;
      this.markAnchor();
    },

    markAnchor: function () {
      var a = this.abAnchor;
      this.$('.ve-ab-anchor').each(function () {
        $(this).toggleClass('ve-active', parseFloat(this.getAttribute('data-ax')) === a.x && parseFloat(this.getAttribute('data-ay')) === a.y);
      });
    },

    onAbPreset: function () {
      var v = this.ui.abPreset.val(), p;
      if (!v) { return; }
      p = v.split('x');
      this.ui.abW.val(this.mmToField(parseFloat(p[0])));
      this.ui.abH.val(this.mmToField(parseFloat(p[1])));
      this._abRatio = parseFloat(p[1]) / parseFloat(p[0]);
    },

    onAbSide: function (e) {
      var w = parseFloat(this.ui.abW.val()), h = parseFloat(this.ui.abH.val()), lock = this.ui.abLock.prop('checked') || this.ui.abScale.prop('checked');
      this.ui.abPreset.val('');
      if (!lock || !this._abRatio) { return; }
      if ($(e.currentTarget).hasClass('ve-ab-w') && w > 0) {
        this.ui.abH.val(+(w * this._abRatio).toFixed(2));
      } else if ($(e.currentTarget).hasClass('ve-ab-h') && h > 0) {
        this.ui.abW.val(+(h / this._abRatio).toFixed(2));
      }
    },

    onAbScale: function () {
      var on = this.ui.abScale.prop('checked');
      if (on) { this.ui.abLock.prop('checked', true); }
      this.ui.abLock.prop('disabled', on);
    },

    onAbAnchor: function (e) {
      this.abAnchor = { x: parseFloat($(e.currentTarget).attr('data-ax')), y: parseFloat($(e.currentTarget).attr('data-ay')) };
      this.markAnchor();
    },

    swapArtboard: function () {
      var w = this.ui.abW.val(), h = this.ui.abH.val();
      this.ui.abW.val(h);
      this.ui.abH.val(w);
      this.ui.abPreset.val('');
      if (this._abRatio) { this._abRatio = 1 / this._abRatio; }
    },

    applyArtboard: function () {
      var wMm = this.fieldToMm(parseFloat(this.ui.abW.val())), hMm = this.fieldToMm(parseFloat(this.ui.abH.val())), curW = this.vb.w * this.mmPerUnit;
      var scale = this.ui.abScale.prop('checked'), f, nw, nh;
      if (isNaN(wMm) || isNaN(hMm) || wMm <= 0 || hMm <= 0) {
        this.notify(t('vectorEditor.artboardInvalid', 'Enter a width and height greater than zero.'));
        return;
      }
      this.checkpoint();
      if (scale) {
        // same drawing, bigger / smaller page: only the physical size of a unit changes
        f = wMm / curW;
        this.mmPerUnit *= f;
        this.exportPxPerUnit *= f;
      } else {
        nw = wMm / this.mmPerUnit;
        nh = hMm / this.mmPerUnit;
        this.vb = {
          x: this.vb.x - (nw - this.vb.w) * this.abAnchor.x,
          y: this.vb.y - (nh - this.vb.h) * this.abAnchor.y,
          w: nw,
          h: nh
        };
      }
      this.vbDirty = true;
      this.zoomFit();
      this.refresh({ tree: false });
      this.updateNote();
      this.renderArtboard();
    },

    /* ------------------------------------------------------------------ */
    /* Units (mm / px) and artboard dimensions                              */
    /* ------------------------------------------------------------------ */

    unitFactor: function (unit) {
      return (unit || this.unit) === 'mm' ? this.mmPerUnit : this.mmPerUnit * 96 / 25.4;
    },

    toUnit: function (v, unit) { return v * this.unitFactor(unit); },
    fromUnit: function (v) { return v / this.unitFactor(); },

    fmtLen: function (v, unit) {
      var u = unit || this.unit;
      return String(+this.toUnit(v, u).toFixed(u === 'mm' ? 1 : 0));
    },

    fmtSize: function (w, h, unit) {
      return this.fmtLen(w, unit) + ' × ' + this.fmtLen(h, unit) + ' ' + (unit || this.unit);
    },

    makeDim: function (vertical) {
      var line = '#b3b8de', box = document.createElement('div'), label = document.createElement('span'), i, tick;
      box.style.cssText = 'position:absolute;pointer-events:none;' + (vertical ?
        'top:0;bottom:0;right:100%;margin-right:12px;width:0;border-left:1px solid ' + line + ';' :
        'left:0;right:0;bottom:100%;margin-bottom:12px;height:0;border-top:1px solid ' + line + ';');
      label.style.cssText = 'position:absolute;white-space:nowrap;font-size:11px;font-weight:600;color:#6f7392;background:#f4f5fb;padding:0 6px;' + (vertical ?
        'top:50%;left:-1px;transform:translate(-50%,-50%) rotate(-90deg);' : 'left:50%;top:-1px;transform:translate(-50%,-50%);');
      box.appendChild(label);
      for (i = 0; i < 2; i++) {
        tick = document.createElement('i');
        tick.style.cssText = 'position:absolute;background:' + line + ';' + (vertical ?
          'left:-4px;width:8px;height:1px;' + (i ? 'bottom:0;' : 'top:0;') : 'top:-4px;height:8px;width:1px;' + (i ? 'right:0;' : 'left:0;'));
        box.appendChild(tick);
      }
      return { box: box, label: label };
    },

    updateDims: function () {
      if (!this.dimW || !this.root) { return; }
      this.dimW.label.textContent = this.fmtLen(this.vb.w) + ' ' + this.unit;
      this.dimH.label.textContent = this.fmtLen(this.vb.h) + ' ' + this.unit;
      this.$('[data-action="unit-mm"]').toggleClass('ve-unit-active', this.unit === 'mm');
      this.$('[data-action="unit-px"]').toggleClass('ve-unit-active', this.unit === 'px');
      this.ui.geoUnit.text('(' + this.unit + ')');
      this.ui.abUnit.text('(' + this.unit + ')');
      this.ui.abMarginUnit.text(this.unit);
    },

    setUnit: function (unit) {
      this.unit = unit;
      try {
        window.localStorage.setItem('ve-unit', unit);
      } catch (err) { /* storage blocked: the choice just is not remembered */ }
      this.updateDims();
      this.updateUi();
      this.updateNote();
      this.ui.geoPanel.find('input').each(function () { this.blur(); });
      this.syncGeometry();
    },

    /* ------------------------------------------------------------------ */
    /* Insert tools (text / rectangle / ellipse)                            */
    /* ------------------------------------------------------------------ */

    setInsertTool: function (name) {
      this.insertTool = (this.insertTool === name) ? null : name;
      this.updateInsertUi();
    },

    updateInsertUi: function () {
      var tool = this.insertTool;
      this.$('[data-action="insert-text"]').toggleClass('ve-active', tool === 'text');
      this.$('[data-action="insert-rect"]').toggleClass('ve-active', tool === 'rect');
      this.$('[data-action="insert-ellipse"]').toggleClass('ve-active', tool === 'ellipse');
      this.ui.stage.css('cursor', tool ? 'crosshair' : '');
    },

    levelPoint: function (cx, cy) {
      var p = safeInverse(screenM(this.level)).transformPoint({ x: cx, y: cy });
      return { x: p.x, y: p.y };
    },

    drawPreview: function (d, ev) {
      var host = this.ui.docHost[0].getBoundingClientRect(), x0 = d.x - host.left, y0 = d.y - host.top;
      var x1 = ev.clientX - host.left, y1 = ev.clientY - host.top, w = Math.abs(x1 - x0), h = Math.abs(y1 - y0), l, tp, el;
      if (ev.shiftKey) {
        w = Math.max(w, h);
        h = w;
      }
      l = x1 >= x0 ? x0 : x0 - w;
      tp = y1 >= y0 ? y0 : y0 - h;
      this.drawOverlay();
      el = d.tool === 'ellipse' ? svgNode('ellipse', { cx: l + w / 2, cy: tp + h / 2, rx: w / 2, ry: h / 2 }) :
        svgNode('rect', { x: l, y: tp, width: w, height: h });
      el.setAttribute('fill', 'rgba(90,99,176,.12)');
      el.setAttribute('stroke', TEAL);
      el.setAttribute('stroke-dasharray', '4 3');
      this.overlay.appendChild(el);
    },

    finishDraw: function (d, ev) {
      var p0 = this.levelPoint(d.x, d.y), p1 = this.levelPoint(ev.clientX, ev.clientY);
      var base = Math.max(this.vb.w, this.vb.h) / 6, dx = p1.x - p0.x, dy = p1.y - p0.y, moved = d.moved && d.tool !== 'text';
      var x, y, w, h, el, fs;
      if (moved && (Math.abs(dx) < base / 50 || Math.abs(dy) < base / 50)) { moved = false; }
      if (d.tool === 'text') {
        fs = Math.max(8, Math.round(Math.min(this.vb.w, this.vb.h) / 10));
        el = svgNode('text', { x: fmt(p0.x), y: fmt(p0.y), 'font-family': FONTS[0], 'font-size': fs, fill: '#000000' });
        el.setAttributeNS(XML_NS, 'xml:space', 'preserve');
        el.appendChild(document.createTextNode(t('vectorEditor.defaultText', 'Text')));
      } else {
        if (moved) {
          w = Math.abs(dx);
          h = Math.abs(dy);
          if (ev.shiftKey) {
            w = Math.max(w, h);
            h = w;
          }
          x = dx >= 0 ? p0.x : p0.x - w;
          y = dy >= 0 ? p0.y : p0.y - h;
        } else {
          w = base;
          h = d.tool === 'rect' ? base * 0.7 : base;
          x = p0.x - w / 2;
          y = p0.y - h / 2;
        }
        if (d.tool === 'rect') {
          el = svgNode('rect', { x: fmt(x), y: fmt(y), width: fmt(w), height: fmt(h), fill: DEFAULT_FILL });
        } else if (Math.abs(w - h) < Math.max(w, h) * 0.005) {
          el = svgNode('circle', { cx: fmt(x + w / 2), cy: fmt(y + h / 2), r: fmt(w / 2), fill: DEFAULT_FILL });
        } else {
          el = svgNode('ellipse', { cx: fmt(x + w / 2), cy: fmt(y + h / 2), rx: fmt(w / 2), ry: fmt(h / 2), fill: DEFAULT_FILL });
        }
      }
      this.checkpoint();
      this.level.appendChild(el);
      this.insertTool = null;
      this.updateInsertUi();
      this.selection = [el];
      this.refresh({ tree: true });
      this.updateNote();
      if (d.tool === 'text') { this.ui.textContent.focus().select(); }
    },

    /* ------------------------------------------------------------------ */
    /* Text properties                                                      */
    /* ------------------------------------------------------------------ */

    textsSel: function () {
      return _.filter(this.editable(), function (el) { return el.localName === 'text'; });
    },

    applyTextProp: function (prop, value) {
      _.each(this.textsSel(), function (tx) {
        _.each(textNodes(tx), function (n) { n.style.setProperty(prop, value); });
      });
    },

    setFontSelect: function (fam) {
      var first = firstFamily(fam).toLowerCase(), match = _.find(FONTS, function (f) { return firstFamily(f).toLowerCase() === first; });
      this.ui.fontFamily.find('.ve-font-custom').remove();
      if (!match) {
        this.ui.fontFamily.append('<option class="ve-font-custom" value="' + _.escape(fam) + '">' + _.escape(firstFamily(fam)) + '</option>');
        match = fam;
      }
      this.ui.fontFamily.val(match);
    },

    syncText: function () {
      var texts = this.textsSel(), cs, active = document.activeElement, w, anchor, ls;
      this.ui.textPanel.css('display', texts.length ? '' : 'none');
      if (!texts.length) { return; }
      cs = window.getComputedStyle(texts[0]);
      this.ui.textContent.prop('disabled', texts.length !== 1);
      if (texts.length === 1 && active !== this.ui.textContent[0]) { this.ui.textContent.val(textContentOf(texts[0])); }
      this.setFontSelect(cs.getPropertyValue('font-family'));
      if (active !== this.ui.fontSize[0]) { this.ui.fontSize.val(Math.round(parseFloat(cs.getPropertyValue('font-size')) * 100) / 100 || ''); }
      ls = parseFloat(cs.getPropertyValue('letter-spacing'));
      if (active !== this.ui.letterSpacing[0]) { this.ui.letterSpacing.val(isNaN(ls) ? 0 : ls); }
      w = cs.getPropertyValue('font-weight');
      anchor = cs.getPropertyValue('text-anchor') || 'start';
      this.$('[data-fmt="bold"]').toggleClass('ve-fmt-active', parseInt(w, 10) >= 600 || w === 'bold');
      this.$('[data-fmt="italic"]').toggleClass('ve-fmt-active', /italic|oblique/.test(cs.getPropertyValue('font-style')));
      this.$('[data-fmt="underline"]').toggleClass('ve-fmt-active', /underline/.test(cs.getPropertyValue('text-decoration-line') || cs.getPropertyValue('text-decoration')));
      this.$('[data-align]').each(function () {
        $(this).toggleClass('ve-fmt-active', this.getAttribute('data-align') === anchor);
      });
    },

    onTextContent: function () {
      var texts = this.textsSel();
      if (texts.length !== 1) { return; }
      this.beginInteractive();
      setTextContent(texts[0], this.ui.textContent.val());
      this.drawOverlay();
      this.scheduleTree();
    },

    onFontFamily: function () {
      if (!this.textsSel().length) { return; }
      this.checkpoint();
      this.applyTextProp('font-family', this.ui.fontFamily.val());
      this.drawOverlay();
    },

    onFontSize: function () {
      var v = parseFloat(this.ui.fontSize.val());
      if (isNaN(v) || v <= 0 || !this.textsSel().length) { return; }
      this.beginInteractive();
      this.applyTextProp('font-size', v + 'px');
      this.drawOverlay();
    },

    onLetterSpacing: function () {
      var v = parseFloat(this.ui.letterSpacing.val());
      if (isNaN(v) || !this.textsSel().length) { return; }
      this.beginInteractive();
      this.applyTextProp('letter-spacing', v + 'px');
      this.drawOverlay();
    },

    onFmt: function (e) {
      var kind = $(e.currentTarget).attr('data-fmt'), texts = this.textsSel(), cs, w, on;
      if (!texts.length) { return; }
      cs = window.getComputedStyle(texts[0]);
      this.checkpoint();
      if (kind === 'bold') {
        w = cs.getPropertyValue('font-weight');
        on = !(parseInt(w, 10) >= 600 || w === 'bold');
        this.applyTextProp('font-weight', on ? '700' : '400');
      } else if (kind === 'italic') {
        on = !/italic|oblique/.test(cs.getPropertyValue('font-style'));
        this.applyTextProp('font-style', on ? 'italic' : 'normal');
      } else {
        on = !/underline/.test(cs.getPropertyValue('text-decoration-line') || cs.getPropertyValue('text-decoration'));
        this.applyTextProp('text-decoration', on ? 'underline' : 'none');
      }
      this.syncText();
      this.drawOverlay();
    },

    // Switch alignment but keep the text where it is (left edge / centre / right edge stays put).
    onAlign: function (e) {
      var anchor = $(e.currentTarget).attr('data-align'), texts = this.textsSel(), self = this;
      if (!texts.length) { return; }
      this.checkpoint();
      _.each(texts, function (tx) {
        var b = tx.getBoundingClientRect(), a, dx;
        _.each(textNodes(tx), function (n) { n.style.setProperty('text-anchor', anchor); });
        a = tx.getBoundingClientRect();
        if (anchor === 'start') {
          dx = b.left - a.left;
        } else if (anchor === 'end') {
          dx = b.right - a.right;
        } else {
          dx = (b.left + b.right) / 2 - (a.left + a.right) / 2;
        }
        if (Math.abs(dx) > 0.01) { self.applyScreenMatrix(self.makeItems([tx]), mTranslate(dx, 0)); }
      });
      this.refresh({ tree: false });
    },

    /* ------------------------------------------------------------------ */
    /* Size & position                                                      */
    /* ------------------------------------------------------------------ */

    docBox: function (els) {
      var rects = this.clientRects(els), u, inv, pts, xs, ys;
      if (!rects.length) { return null; }
      u = rectUnion(rects);
      inv = safeInverse(screenM(this.root));
      pts = _.map([[u.l, u.t], [u.r, u.t], [u.r, u.b], [u.l, u.b]], function (p) { return inv.transformPoint({ x: p[0], y: p[1] }); });
      xs = _.pluck(pts, 'x');
      ys = _.pluck(pts, 'y');
      return {
        x: Math.min.apply(null, xs), y: Math.min.apply(null, ys),
        w: Math.max.apply(null, xs) - Math.min.apply(null, xs), h: Math.max.apply(null, ys) - Math.min.apply(null, ys)
      };
    },

    syncGeometry: function () {
      var sel = this.editable(), box, active = document.activeElement, single, self = this;
      this.ui.geoPanel.css('display', sel.length ? '' : 'none');
      if (!sel.length) { return; }
      box = this.docBox(sel);
      if (!box) { return; }
      _.each([['geoX', 'x'], ['geoY', 'y'], ['geoW', 'w'], ['geoH', 'h']], function (p) {
        var input = self.ui[p[0]][0];
        var v = (p[1] === 'x' ? box.x - self.vb.x : (p[1] === 'y' ? box.y - self.vb.y : box[p[1]]));
        if (active !== input) { input.value = +self.toUnit(v).toFixed(self.unit === 'mm' ? 2 : 1); }
      });
      single = sel.length === 1 && sel[0].localName === 'rect';
      this.ui.geoRadiusRow.css('display', single ? '' : 'none');
      if (single && active !== this.ui.geoRadius[0]) {
        this.ui.geoRadius.val(+this.toUnit(parseFloat(sel[0].getAttribute('rx')) || 0).toFixed(this.unit === 'mm' ? 2 : 1));
      }
    },

    onGeoChange: function (e) {
      var sel = this.editable(), box = this.docBox(sel), $t = $(e.currentTarget), lock = this.ui.geoLock.prop('checked');
      var nx = parseFloat(this.ui.geoX.val()), ny = parseFloat(this.ui.geoY.val()), nw = parseFloat(this.ui.geoW.val()), nh = parseFloat(this.ui.geoH.val());
      if (!box || isNaN(nx) || isNaN(ny) || isNaN(nw) || isNaN(nh)) { return; }
      nx = this.fromUnit(nx) + this.vb.x;
      ny = this.fromUnit(ny) + this.vb.y;
      nw = this.fromUnit(nw);
      nh = this.fromUnit(nh);
      if (nw <= 0 || nh <= 0) {
        this.syncGeometry();
        return;
      }
      if (lock && box.w > 0 && box.h > 0) {
        if ($t.hasClass('ve-geo-w')) {
          nh = nw * box.h / box.w;
        } else if ($t.hasClass('ve-geo-h')) {
          nw = nh * box.w / box.h;
        }
      }
      this.applyGeometry(sel, box, nx, ny, nw, nh);
    },

    applyGeometry: function (sel, box, nx, ny, nw, nh) {
      var R = screenM(this.root), Mroot;
      this.checkpoint();
      if (sel.length === 1 && this.directGeometry(sel[0], nx, ny, nw, nh)) {
        this.refresh({ tree: false });
        return;
      }
      Mroot = mTranslate(nx - box.x, ny - box.y).multiply(mTranslate(box.x, box.y))
        .multiply(mScale(box.w > 0 ? nw / box.w : 1, box.h > 0 ? nh / box.h : 1)).multiply(mTranslate(-box.x, -box.y));
      this.applyScreenMatrix(this.makeItems(sel), R.multiply(Mroot).multiply(safeInverse(R)));
      this.refresh({ tree: false });
    },

    // Plain rect / circle / ellipse in an untransformed context: edit the real geometry
    // instead of adding a transform (keeps stroke widths and numbers clean).
    directGeometry: function (el, nx, ny, nw, nh) {
      var ln = el.localName, ell, i, a;
      if (!(ln === 'rect' || ln === 'ellipse' || ln === 'circle') || el.getAttribute('transform')) { return false; }
      if (!sameM(screenM(el.parentNode), screenM(this.root))) { return false; }
      if (ln === 'rect') {
        el.setAttribute('x', fmt(nx));
        el.setAttribute('y', fmt(ny));
        el.setAttribute('width', fmt(nw));
        el.setAttribute('height', fmt(nh));
      } else if (ln === 'ellipse') {
        el.setAttribute('cx', fmt(nx + nw / 2));
        el.setAttribute('cy', fmt(ny + nh / 2));
        el.setAttribute('rx', fmt(nw / 2));
        el.setAttribute('ry', fmt(nh / 2));
      } else if (Math.abs(nw - nh) < 1e-6 * Math.max(nw, nh)) {
        el.setAttribute('cx', fmt(nx + nw / 2));
        el.setAttribute('cy', fmt(ny + nh / 2));
        el.setAttribute('r', fmt(nw / 2));
      } else {
        ell = svgNode('ellipse');
        for (i = 0; i < el.attributes.length; i++) {
          a = el.attributes[i];
          if (a.name !== 'r' && a.name !== 'cx' && a.name !== 'cy') { ell.setAttribute(a.name, a.value); }
        }
        ell.setAttribute('cx', fmt(nx + nw / 2));
        ell.setAttribute('cy', fmt(ny + nh / 2));
        ell.setAttribute('rx', fmt(nw / 2));
        ell.setAttribute('ry', fmt(nh / 2));
        el.parentNode.replaceChild(ell, el);
        this.selection = [ell];
      }
      return true;
    },

    // Centre the selection on the artboard, or scale it (proportionally) to fit inside and centre it.
    alignSel: function (mode) {
      var sel = this.editable(), box = this.docBox(sel), cx, cy, s, nw, nh, nx, ny;
      if (!box) { return; }
      cx = this.vb.x + this.vb.w / 2;
      cy = this.vb.y + this.vb.h / 2;
      nw = box.w;
      nh = box.h;
      if (mode === 'fit-artboard') {
        s = Math.min(box.w > 0 ? this.vb.w / box.w : Infinity, box.h > 0 ? this.vb.h / box.h : Infinity);
        if (!isFinite(s) || s <= 0) { return; }
        nw = box.w * s;
        nh = box.h * s;
      }
      nx = mode === 'center-v' ? box.x : cx - nw / 2;
      ny = mode === 'center-h' ? box.y : cy - nh / 2;
      this.applyGeometry(sel, box, nx, ny, nw, nh);
    },

    onGeoRadius: function () {
      var sel = this.editable(), v = this.fromUnit(Math.max(0, parseFloat(this.ui.geoRadius.val()) || 0));
      if (sel.length !== 1 || sel[0].localName !== 'rect') { return; }
      this.checkpoint();
      sel[0].setAttribute('rx', fmt(v));
      sel[0].setAttribute('ry', fmt(v));
      this.refresh({ tree: false });
    },

    /* ------------------------------------------------------------------ */
    /* Copy / cut / paste                                                   */
    /* ------------------------------------------------------------------ */

    isBlocked: function () { return $('.image-editor-view').length > 0; },

    isTyping: function (target) {
      var tag = (target && target.tagName) || '';
      return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || !!(target && target.isContentEditable);
    },

    // Bake the CSS-driven paint of the original into inline styles so a copy keeps its
    // look even though the style sheet that styled it stays behind.
    bakeStyles: function (orig, clone) {
      var a = shapesIn(orig), b = shapesIn(clone), i, j, cs, v;
      for (i = 0; i < a.length && i < b.length; i++) {
        cs = window.getComputedStyle(a[i]);
        for (j = 0; j < BAKE.length; j++) {
          v = cs.getPropertyValue(BAKE[j]);
          if (v) { b[i].style.setProperty(BAKE[j], v.replace(/url\(\s*["']?(#[^"')]+)["']?\s*\)/, 'url($1)')); }
        }
      }
    },

    buildPayload: function (els) {
      var self = this, Rinv = safeInverse(screenM(this.root)), payload = svgNode('svg'), defs = svgNode('defs'), seen = {}, queue = [], id, src, c;
      payload.setAttribute('xmlns', SVG_NS);
      payload.setAttributeNS('http://www.w3.org/2000/xmlns/', 'xmlns:xlink', XLINK_NS);
      payload.setAttribute('viewBox', [this.vb.x, this.vb.y, this.vb.w, this.vb.h].map(fmt).join(' '));
      _.each(docOrder(els), function (el) {
        var clone = el.cloneNode(true), D, wrap;
        self.bakeStyles(el, clone);
        D = Rinv.multiply(screenM(el.parentNode)).multiply(getM(el));
        clone.removeAttribute('transform');
        wrap = svgNode('g', { 'data-ve-item': '1', 'data-ve-d': [D.a, D.b, D.c, D.d, D.e, D.f].join(',') });
        wrap.appendChild(clone);
        payload.appendChild(wrap);
        queue = queue.concat(refsIn(clone));
      });
      while (queue.length) {
        id = queue.shift();
        if (seen[id]) { continue; }
        seen[id] = true;
        if (payload.querySelector('[id="' + id.replace(/(["\\])/g, '\\$1') + '"]')) { continue; }
        src = this.shadow.getElementById(id);
        if (!src) { continue; }
        c = src.cloneNode(true);
        defs.appendChild(c);
        queue = queue.concat(refsIn(c));
      }
      if (defs.firstChild) { payload.insertBefore(defs, payload.firstChild); }
      _.each(payload.querySelectorAll('[data-ve-locked]'), function (n) { n.removeAttribute('data-ve-locked'); });
      return new XMLSerializer().serializeToString(payload);
    },

    copySel: function (cut, clipData) {
      var sel = this.editable(), payload;
      if (!sel.length) { return; }
      payload = this.buildPayload(sel);
      clipboardStore = { text: payload };
      this._pasteN = 0;
      if (clipData) {
        clipData.setData('text/plain', payload);
      } else if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(payload).then(null, function () { /* clipboard blocked: the in-app copy still works */ });
      }
      if (cut) { this.deleteSel(); }
    },

    onClipEvent: function (e, cut) {
      if (this.isBlocked() || this.isTyping(e.target) || !this.root || !this.editable().length) { return; }
      e.preventDefault();
      this.copySel(cut, e.clipboardData);
    },

    onPasteEvent: function (e) {
      var txt;
      if (this.isBlocked() || this.isTyping(e.target) || !this.root) { return; }
      txt = e.clipboardData && e.clipboardData.getData('text/plain');
      if (txt && /<svg[\s>]/i.test(txt)) {
        e.preventDefault();
        this.pasteSvg(txt);
      } else if (clipboardStore) {
        e.preventDefault();
        this.pasteSvg(clipboardStore.text);
      }
    },

    pasteButton: function () {
      var self = this;
      function fallback() {
        if (clipboardStore) {
          self.pasteSvg(clipboardStore.text);
        } else {
          self.notify(t('vectorEditor.nothingToPaste', 'Nothing to paste. Copy objects first.'));
        }
      }
      if (navigator.clipboard && navigator.clipboard.readText) {
        navigator.clipboard.readText().then(function (txt) {
          if (/<svg[\s>]/i.test(txt || '')) { self.pasteSvg(txt); } else { fallback(); }
        }, fallback);
      } else {
        fallback();
      }
    },

    pasteSvg: function (text) {
      var self = this, parsed, tmp, items = [], defsKids = [], defs, Linv, offset, pasted = [];
      try {
        parsed = parseSvgText(text);
      } catch (err) {
        this.notify(t('vectorEditor.nothingToPaste', 'Nothing to paste. Copy objects first.'));
        return;
      }
      tmp = svgNode('g');
      _.each(kids(parsed.root), function (k) {
        if (k.localName === 'defs') {
          _.each(kids(k), function (n) {
            defsKids.push(n);
            tmp.appendChild(n);
          });
        } else if (RENDER[k.localName]) {
          items.push(k);
          tmp.appendChild(k);
        }
      });
      if (!items.length) {
        this.notify(t('vectorEditor.nothingToPaste', 'Nothing to paste. Copy objects first.'));
        return;
      }
      this.uniquifyIds(tmp);
      this.checkpoint();
      if (defsKids.length) {
        defs = _.find(kids(this.root), function (k) { return k.localName === 'defs'; });
        if (!defs) {
          defs = svgNode('defs');
          this.root.insertBefore(defs, this.root.firstChild);
        }
        _.each(defsKids, function (n) { defs.appendChild(n); });
      }
      this._pasteN += 1;
      offset = Math.max(this.vb.w, this.vb.h) * 0.02 * this._pasteN;
      Linv = safeInverse(safeInverse(screenM(this.root)).multiply(screenM(this.level)));
      _.each(items, function (it) {
        var el = it, D = new DOMMatrix(), n;
        if (it.getAttribute('data-ve-item') === '1' && it.children.length === 1) {
          el = it.firstElementChild;
          n = _.map((it.getAttribute('data-ve-d') || '').split(','), parseFloat);
          if (n.length === 6 && _.every(n, isFinite)) { D = new DOMMatrix(n); }
        }
        setM(el, Linv.multiply(mTranslate(offset, offset)).multiply(D));
        self.level.appendChild(el);
        pasted.push(el);
      });
      this.selection = pasted;
      this.refresh({ tree: true });
      this.updateNote();
    },

    /* ------------------------------------------------------------------ */
    /* Pictures inside the artwork -> image editor                          */
    /* ------------------------------------------------------------------ */

    imagesInSel: function () {
      var list = [];
      _.each(this.editable(), function (el) {
        if (el.localName === 'image') { list.push(el); }
        list = list.concat(Array.prototype.slice.call(el.querySelectorAll('image')));
      });
      return list;
    },

    editImage: function (target) {
      var imgs = target ? [target] : this.imagesInSel(), img, href, self = this;
      if (!imgs.length) {
        this.notify(t('vectorEditor.noImage', 'Select a picture first.'));
        return;
      }
      if (imgs.length > 1) {
        this.notify(imgs.length + ' ' + t('vectorEditor.manyImages', 'pictures in the selection — select a single picture (Ctrl/Cmd+click picks an object inside a group).'));
        return;
      }
      img = imgs[0];
      href = img.getAttribute('href') || img.getAttributeNS(XLINK_NS, 'href');
      if (!href) {
        this.notify(t('vectorEditor.noImageSrc', 'This picture has no editable source.'));
        return;
      }
      ImageEditorView.open({
        url: href,
        fileName: 'image.png',
        title: t('vectorEditor.editImageTitle', 'Edit image'),
        onSave: function (file) {
          var reader = new FileReader();
          reader.onload = function () { self.replaceImageData(img, reader.result); };
          reader.readAsDataURL(file);
        }
      });
    },

    // The picture keeps its frame; if the edit changed the aspect ratio (crop) it is
    // fitted inside the old frame instead of being stretched.
    replaceImageData: function (img, dataUrl) {
      var probe = new Image(), self = this;
      probe.onload = function () {
        var w = parseFloat(img.getAttribute('width')), h = parseFloat(img.getAttribute('height')), same;
        if (!img.parentNode || !self.root.contains(img)) { return; }
        self.checkpoint();
        if (img.hasAttribute('href')) {
          img.setAttribute('href', dataUrl);
        } else {
          img.setAttributeNS(XLINK_NS, 'xlink:href', dataUrl);
        }
        same = w > 0 && h > 0 && Math.abs(w / h - probe.naturalWidth / probe.naturalHeight) < 0.01 * (w / h);
        img.setAttribute('preserveAspectRatio', same ? 'none' : 'xMidYMid meet');
        self.refresh({ tree: true });
        self.updateNote();
      };
      probe.src = dataUrl;
    },

    selectionShapes: function () {
      var list = [];
      _.each(this.editable(), function (el) { list = list.concat(shapesIn(el)); });
      return list;
    },

    syncAppearance: function () {
      var shapes = this.selectionShapes(), el, cs, fill, stroke, fh, sh, op, enabled;
      var sel = this.editable();
      enabled = shapes.length > 0 || sel.length > 0;
      this.ui.fill.add(this.ui.fillNone).add(this.ui.stroke).add(this.ui.strokeNone)
        .add(this.ui.strokeWidth).add(this.ui.alpha).prop('disabled', !enabled);
      if (!enabled) {
        this.ui.alpha.val(0);
        this.ui.alphaVal.text('0%');
        return;
      }
      if (shapes.length) {
        el = shapes[0];
        cs = window.getComputedStyle(el);
        fill = cs.getPropertyValue('fill');
        stroke = cs.getPropertyValue('stroke');
        fh = toHex(fill);
        sh = toHex(stroke);
        this.ui.fillNone.prop('checked', fill === 'none');
        this.ui.strokeNone.prop('checked', stroke === 'none');
        if (fh) { this.ui.fill.val(fh); }
        if (sh) { this.ui.stroke.val(sh); }
        if (!fh && fill !== 'none') {
          fh = this.firstStopHex(fill);
          if (fh) { this.ui.fill.val(fh); }
        }
        this.ui.strokeWidth.val(stroke === 'none' ? 0 : (parseFloat(cs.getPropertyValue('stroke-width')) || 0));
      }
      op = parseFloat(window.getComputedStyle(sel.length ? sel[0] : shapes[0]).opacity);
      op = isNaN(op) ? 1 : op;
      this.setAlphaUi(Math.round((1 - op) * 100));
    },

    setAlphaUi: function (pct) {
      this.ui.alpha.val(pct);
      this.ui.alphaVal.text(pct + '%');
      this.ui.ctxAlpha.val(pct);
      this.ui.ctxAlphaVal.text(pct + '%');
      this.ui.ctxBadge.text(pct ? pct + '%' : '');
    },

    stopsOf: function (value) {
      var m = /url\(\s*["']?#([^"')]+)["']?\s*\)/.exec(value || ''), g, guard = 0, stops = [];
      if (!m) { return stops; }
      g = this.shadow.getElementById(m[1]);
      while (g && guard++ < 6) {
        stops = _.filter(kids(g), function (k) { return k.localName === 'stop'; });
        if (stops.length) { break; }
        m = (g.getAttribute('href') || g.getAttributeNS(XLINK_NS, 'href') || '').replace(/^#/, '');
        g = m ? this.shadow.getElementById(m) : null;
      }
      return stops;
    },

    firstStopHex: function (value) {
      var stops = this.stopsOf(value);
      return stops.length ? toHex(window.getComputedStyle(stops[0]).getPropertyValue('stop-color')) : null;
    },

    setPaint: function (prop, value) {
      var shapes = this.selectionShapes();
      if (!shapes.length) { return; }
      _.each(shapes, function (el) { el.style.setProperty(prop, value); });
    },

    onFillInput: function () {
      this.beginInteractive();
      this.ui.fillNone.prop('checked', false);
      this.setPaint('fill', this.ui.fill.val());
    },

    onFillNone: function () {
      this.checkpoint();
      this.setPaint('fill', this.ui.fillNone.prop('checked') ? 'none' : this.ui.fill.val());
      this.scheduleColors();
    },

    onStrokeInput: function () {
      this.beginInteractive();
      this.ui.strokeNone.prop('checked', false);
      this.setPaint('stroke', this.ui.stroke.val());
      if (parseFloat(this.ui.strokeWidth.val()) <= 0 || isNaN(parseFloat(this.ui.strokeWidth.val()))) {
        this.ui.strokeWidth.val(1);
        this.setPaint('stroke-width', '1');
      }
      this.drawOverlay();
    },

    onStrokeNone: function () {
      this.checkpoint();
      this.setPaint('stroke', this.ui.strokeNone.prop('checked') ? 'none' : this.ui.stroke.val());
      this.scheduleColors();
    },

    onStrokeWidth: function () {
      var w = Math.max(0, parseFloat(this.ui.strokeWidth.val()) || 0);
      this.beginInteractive();
      this.setPaint('stroke-width', String(w));
      if (w > 0 && this.ui.strokeNone.prop('checked')) {
        this.ui.strokeNone.prop('checked', false);
        this.setPaint('stroke', this.ui.stroke.val());
      }
      this.drawOverlay();
    },

    applyAlpha: function (pct) {
      var sel = this.editable();
      if (!sel.length) { return; }
      this.beginInteractive();
      _.each(sel, function (el) { el.style.setProperty('opacity', String(+(1 - pct / 100).toFixed(3))); });
      this.setAlphaUi(pct);
    },

    onAlphaInput: function () { this.applyAlpha(parseInt(this.ui.alpha.val(), 10) || 0); },
    onCtxAlphaInput: function () { this.applyAlpha(parseInt(this.ui.ctxAlpha.val(), 10) || 0); },

    /* ------------------------------------------------------------------ */
    /* Colour inventory / recolour                                          */
    /* ------------------------------------------------------------------ */

    scheduleColors: function () {
      var self = this;
      clearTimeout(this._colorTimer);
      this._colorTimer = setTimeout(function () {
        self.renderColors();
        self.refreshTool();
      }, 80);
    },

    collectColors: function (forceAll) {
      var sel = forceAll ? [] : this.editable(), shapes = [], map = {}, order = [], self = this, seenStops = [];
      if (sel.length) {
        _.each(sel, function (el) { shapes = shapes.concat(shapesIn(el)); });
      } else {
        shapes = shapesIn(this.root);
      }
      shapes = shapes.slice(0, MAX_COLOR_SHAPES);
      function add(hex, el, prop) {
        if (!hex) { return; }
        if (!map[hex]) {
          map[hex] = { hex: hex, count: 0, refs: [] };
          order.push(hex);
        }
        map[hex].count++;
        map[hex].refs.push({ el: el, prop: prop });
      }
      function consider(el, prop, value) {
        var hex = toHex(value);
        if (hex) {
          add(hex, el, prop);
        } else if (/^url\(/.test(value || '')) {
          _.each(self.stopsOf(value), function (stop) {
            if (!_.contains(seenStops, stop)) {
              seenStops.push(stop);
              add(toHex(window.getComputedStyle(stop).getPropertyValue('stop-color')), stop, 'stop-color');
            }
          });
        }
      }
      _.each(shapes, function (el) {
        var cs, isSpan = el.localName === 'tspan' || el.localName === 'textPath';
        if (isHidden(el)) { return; }
        cs = window.getComputedStyle(el);
        if (!isSpan || el.getAttribute('fill') || el.style.fill) { consider(el, 'fill', cs.getPropertyValue('fill')); }
        if (!isSpan || el.getAttribute('stroke') || el.style.stroke) { consider(el, 'stroke', cs.getPropertyValue('stroke')); }
      });
      return {
        scoped: sel.length > 0,
        entries: _.first(_.sortBy(_.map(order, function (h) { return map[h]; }), function (e) { return -e.count; }), 60)
      };
    },

    renderColors: function () {
      var res, html = '';
      if (!this.root) { return; }
      res = this.collectColors();
      this._colorEntries = res.entries;
      this._colorScoped = res.scoped;
      this.ui.colorsScope.text(res.scoped ? '(' + t('vectorEditor.selection', 'selection') + ')' : '(' + t('vectorEditor.all', 'all') + ')');
      if (!res.entries.length) {
        this.ui.colorsList.html('<div class="ve-colors-empty">' + _.escape(t('vectorEditor.noColors', 'No solid colors found.')) + '</div>');
        return;
      }
      _.each(res.entries, function (e, i) {
        html += '<div class="ve-color-row" data-idx="' + i + '"><input type="color" value="' + e.hex + '" data-old="' + e.hex + '">' +
          '<span class="ve-color-hex">' + e.hex + '</span><span class="ve-color-cmyk">' + cmykLabel(e.hex) + '</span><span class="ve-color-count">' + e.count + '×</span></div>';
      });
      this.ui.colorsList.html(html);
    },

    /* ------------------------------------------------------------------ */
    /* Replace / reduce colours tools                                       */
    /* ------------------------------------------------------------------ */

    setTool: function (name) {
      this.tool = (this.tool === name) ? null : name;
      this.ui.replacePanel.css('display', this.tool === 'replace' ? '' : 'none');
      this.ui.reducePanel.css('display', this.tool === 'reduce' ? '' : 'none');
      this.ui.abPanel.css('display', this.tool === 'artboard' ? '' : 'none');
      this.$('[data-action="tool-artboard"]').toggleClass('ve-active', this.tool === 'artboard');
      this.$('[data-action="tool-replace"]').toggleClass('ve-active', this.tool === 'replace');
      this.$('[data-action="tool-reduce"]').toggleClass('ve-active', this.tool === 'reduce');
      this.refreshTool();
    },

    refreshTool: function () {
      if (!this.root) { return; }
      if (this.tool === 'replace') {
        this.renderReplace();
      } else if (this.tool === 'reduce') {
        this.renderReduce();
      } else if (this.tool === 'artboard') {
        this.renderArtboard();
      }
    },

    scopeLabel: function (scoped) {
      return '(' + (scoped ? t('vectorEditor.selection', 'selection') : t('vectorEditor.all', 'all')) + ')';
    },

    renderReplace: function () {
      var res = this.collectColors(), html = '', self = this;
      this._repEntries = res.entries;
      this.ui.repScope.text(this.scopeLabel(res.scoped));
      if (this._repFrom && !_.findWhere(res.entries, { hex: this._repFrom })) { this._repFrom = null; }
      _.each(res.entries, function (e) {
        html += '<div class="ve-rep-row' + (e.hex === self._repFrom ? ' ve-rep-picked' : '') + '" data-hex="' + e.hex + '">' +
          '<span class="ve-rep-swatch" style="background:' + e.hex + '"></span>' +
          '<span class="ve-rep-hex-label">' + e.hex + '</span><span class="ve-rep-cmyk-label">' + cmykLabel(e.hex) + '</span><span class="ve-rep-count">' + e.count + '×</span></div>';
      });
      this.ui.repList.html(html || '<div class="ve-colors-empty">' + _.escape(t('vectorEditor.noColors', 'No solid colors found.')) + '</div>');
      if (!this.ui.repPalette.children().length) {
        this.ui.repPalette.html(_.map(BASE_COLORS, function (c) {
          return '<button type="button" class="ve-rep-sw" data-hex="' + c + '" title="' + c + '" style="background:' + c + '"></button>';
        }).join(''));
      }
      this.ui.repApply.prop('disabled', !this._repFrom);
      this.ui.repUsed.html(_.map((res.scoped ? this.collectColors(true) : res).entries, function (e) {
        return '<button type="button" class="ve-rep-sw" data-hex="' + e.hex + '" title="' + e.hex + ' · ' + cmykLabel(e.hex) + ' · ' + e.count + '×" style="background:' + e.hex + '"></button>';
      }).join(''));
      if (!this._cmykInit) {
        this._cmykInit = true;
        this.setCmykInputs(this.ui.repColor.val());
      }
    },

    onRepRowClick: function (e) {
      this._repFrom = $(e.currentTarget).attr('data-hex');
      this.ui.repList.find('.ve-rep-row').removeClass('ve-rep-picked');
      $(e.currentTarget).addClass('ve-rep-picked');
      this.ui.repApply.prop('disabled', false);
    },

    setCmykInputs: function (hex) {
      var c = rgbToCmyk(hex);
      this.$('.ve-rep-cmyk').each(function () {
        this.value = c[{ c: 0, m: 1, y: 2, k: 3 }[this.getAttribute('data-ch')]];
      });
    },

    onRepCmyk: function () {
      var v = {}, hex;
      this.$('.ve-rep-cmyk').each(function () { v[this.getAttribute('data-ch')] = this.value; });
      hex = cmykToHex(v.c, v.m, v.y, v.k);
      this.ui.repColor.val(hex);
      this.ui.repHex.val(hex.toUpperCase());
    },

    setRepColor: function (hex) {
      this.ui.repColor.val(hex);
      this.ui.repHex.val(hex.toUpperCase());
      this.setCmykInputs(hex);
    },

    onRepSwatch: function (e) {
      this.setRepNone(false);
      this.setRepColor($(e.currentTarget).attr('data-hex'));
    },

    repNoneOn: function () { return this.ui.repNone.hasClass('ve-active'); },

    setRepNone: function (on) {
      this.ui.repNone.toggleClass('ve-active', !!on);
      this.ui.repColor.add(this.ui.repHex).add(this.$('.ve-rep-cmyk')).prop('disabled', !!on);
    },

    onRepNone: function () { this.setRepNone(!this.repNoneOn()); },
    onRepColorInput: function () {
      this.setRepNone(false);
      this.ui.repHex.val(this.ui.repColor.val().toUpperCase());
      this.setCmykInputs(this.ui.repColor.val());
    },

    onRepHexInput: function () {
      var v = this.ui.repHex.val();
      if (v.charAt(0) !== '#') { v = '#' + v; }
      if (/^#[0-9a-f]{6}$/i.test(v)) {
        this.ui.repColor.val(v.toLowerCase());
        this.setCmykInputs(v.toLowerCase());
      }
    },

    onRepTol: function () { this.ui.repTolVal.text(this.ui.repTol.val() + '%'); },

    replaceApply: function () {
      var none = this.repNoneOn();
      var from = this._repFrom, to = none ? 'none' : this.ui.repColor.val().toLowerCase(), tol = (parseInt(this.ui.repTol.val(), 10) || 0) / 100 * 441.67;
      var fromRgb, hits = [], count = 0;
      if (!from || (!none && !/^#[0-9a-f]{6}$/.test(to))) { return; }
      fromRgb = hexRgb(from);
      hits = _.filter(this._repEntries || [], function (e) {
        return e.hex === from || Math.sqrt(rgbDist2(hexRgb(e.hex), fromRgb)) <= tol;
      });
      _.each(hits, function (e) {
        _.each(e.refs, function (r) { if (r.el.parentNode) { count++; } });
      });
      if (!count || (hits.length === 1 && hits[0].hex === to)) { return; }
      this.checkpoint();
      _.each(hits, function (e) {
        _.each(e.refs, function (r) {
          // a gradient stop cannot be "none" — make it fully transparent instead
          if (none && r.prop === 'stop-color') {
            r.el.style.setProperty('stop-opacity', '0');
          } else {
            r.el.style.setProperty(r.prop, to);
          }
        });
      });
      this._repFrom = none ? null : to;
      this.renderColors();
      this.renderReplace();
      this.syncAppearance();
      this.scheduleTree();
      this.notify(count + ' ' + t('vectorEditor.colorsReplaced', 'color uses replaced'));
    },

    entryWeights: function (entries) {
      _.each(entries, function (e) {
        var w = 0;
        _.each(e.refs, function (r) {
          var b;
          if (r.prop === 'stop-color') {
            w += 50;
          } else {
            b = r.el.getBoundingClientRect();
            w += Math.max(1, b.width * b.height);
          }
        });
        e.w = w;
      });
    },

    // Temporarily apply a colour map to the live artwork, hand back an undo function.
    applyMap: function (entries, map) {
      var saved = [];
      _.each(entries, function (e) {
        var target = map[e.hex];
        if (!target || target === e.hex) { return; }
        _.each(e.refs, function (r) {
          saved.push({ el: r.el, prop: r.prop, val: r.el.style.getPropertyValue(r.prop), pr: r.el.style.getPropertyPriority(r.prop) });
          r.el.style.setProperty(r.prop, target);
        });
      });
      return function () {
        _.each(saved, function (sv) {
          if (sv.val) { sv.el.style.setProperty(sv.prop, sv.val, sv.pr); } else { sv.el.style.removeProperty(sv.prop); }
        });
      };
    },

    thumbUrl: function () {
      var c = this.root.cloneNode(true), w = 120, h = Math.max(20, Math.round(120 * this.vb.h / this.vb.w));
      if (h > 80) {
        h = 80;
        w = Math.max(20, Math.round(80 * this.vb.w / this.vb.h));
      }
      c.setAttribute('width', w);
      c.setAttribute('height', h);
      c.style.overflow = 'hidden';
      return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(new XMLSerializer().serializeToString(c));
    },

    renderReduce: function () {
      var res = this.collectColors(), entries = res.entries, maxN, n, variant, undo, html = '', self = this, label, chips, thumbs;
      this._crCache = {};
      this.ui.crScope.text(this.scopeLabel(res.scoped));
      this.ui.crList.empty();
      maxN = Math.min(6, entries.length - 1);
      this.ui.crEmpty.toggle(maxN < 1);
      if (maxN < 1) { return; }
      this.entryWeights(entries);
      thumbs = new XMLSerializer().serializeToString(this.root).length < 2500000;
      for (n = 1; n <= maxN; n++) {
        variant = reducePalette(_.map(entries, function (e) { return { hex: e.hex, w: e.w }; }), n);
        this._crCache[n] = { entries: entries, map: variant.map };
        label = n === 1 ? t('vectorEditor.colorOne', '1 color') : n + ' ' + t('vectorEditor.colorsPlural', 'colors');
        chips = _.map(variant.palette, function (c) { return '<span class="ve-cr-chip" style="background:' + c + '"></span>'; }).join('');
        undo = null;
        if (thumbs) {
          undo = this.applyMap(entries, variant.map);
        }
        html += '<div class="ve-cr-item" data-n="' + n + '">' +
          (thumbs ? '<div class="ve-cr-thumb"><img src="' + this.thumbUrl() + '"/></div>' : '') +
          '<div class="ve-cr-meta"><span class="ve-cr-label">' + label + '</span><span class="ve-cr-chips">' + chips + '</span></div></div>';
        if (undo) { undo(); }
      }
      self.ui.crList.html(html);
    },

    onCrClick: function (e) {
      var n = parseInt($(e.currentTarget).attr('data-n'), 10), c = this._crCache[n];
      if (!c) { return; }
      this.checkpoint();
      this.applyMap(c.entries, c.map);
      this.setTool(null);
      this.renderColors();
      this.syncAppearance();
      this.scheduleTree();
      this.notify(t('vectorEditor.colorsReduced', 'Colors reduced to') + ' ' + n);
    },

    onColorRowInput: function (e) {
      var input = e.currentTarget, idx = parseInt($(input).closest('.ve-color-row').attr('data-idx'), 10);
      var entry = this._colorEntries[idx], val = input.value;
      if (!entry) { return; }
      this.beginInteractive();
      _.each(entry.refs, function (r) { r.el.style.setProperty(r.prop, val); });
      entry.hex = val;
      $(input).closest('.ve-color-row').find('.ve-color-hex').text(val);
      $(input).closest('.ve-color-row').find('.ve-color-cmyk').text(cmykLabel(val));
      this.drawOverlay();
    },

    onColorRowChange: function () {
      this._iact = false;
      this.renderColors();
      this.syncAppearance();
      this.scheduleTree();
    },

    /* ------------------------------------------------------------------ */
    /* Objects tree                                                         */
    /* ------------------------------------------------------------------ */

    scheduleTree: function () {
      var self = this;
      clearTimeout(this._treeTimer);
      this._treeTimer = setTimeout(function () { self.renderTree(); }, 60);
    },

    revealSelection: function () {
      var changed = false, self = this;
      _.each(this.selection, function (el) {
        var p = el.parentNode;
        while (p && p !== self.root) {
          if (!_.contains(self.expanded, p)) {
            self.expanded.push(p);
            changed = true;
          }
          p = p.parentNode;
        }
      });
      return changed;
    },

    renderTree: function () {
      var rows = [], html = [], self = this;
      if (!this.root) { return; }
      this.revealSelection();
      function walk(parent, depth) {
        var list = renderableKids(parent).reverse(), shown = list.slice(0, MAX_TREE_CHILDREN), i, el, idx, isG, open, cs, fill, hex, cls;
        for (i = 0; i < shown.length; i++) {
          el = shown[i];
          idx = rows.push(el) - 1;
          isG = CONTAINER[el.localName] && renderableKids(el).length > 0;
          open = isG && _.contains(self.expanded, el);
          cs = window.getComputedStyle(el);
          fill = cs.getPropertyValue('fill');
          hex = toHex(fill) || toHex(cs.getPropertyValue('stroke')) || '';
          cls = 've-node-row' + (_.contains(self.selection, el) ? ' ve-selected' : '') +
            (isHidden(el) ? ' ve-is-hidden' : '') + (isLocked(el) ? ' ve-is-locked' : '');
          html.push('<div class="' + cls + '" data-idx="' + idx + '" style="padding-left:' + (4 + depth * 14) + 'px">' +
            '<span class="ve-node-caret" data-caret="1">' + (isG ? (open ? '▾' : '▸') : '') + '</span>' +
            '<span class="ve-node-swatch" style="background:' + (hex || 'transparent') + '"></span>' +
            '<span class="ve-node-label">' + _.escape(typeLabel(el)) + '</span>' +
            '<button type="button" class="ve-node-btn" data-btn="hide" title="' + _.escape(t('vectorEditor.showHide', 'Show / hide')) + '">' + (isHidden(el) ? '○' : '◉') + '</button>' +
            '<button type="button" class="ve-node-btn" data-btn="lock" title="' + _.escape(t('vectorEditor.lockUnlock', 'Lock / unlock')) + '">' + (isLocked(el) ? '🔒' : '·') + '</button>' +
            '</div>');
          if (open) { walk(el, depth + 1); }
        }
        if (list.length > shown.length) {
          html.push('<div class="ve-node-more" style="padding-left:' + (4 + depth * 14 + 18) + 'px">… ' + (list.length - shown.length) + ' ' +
            _.escape(t('vectorEditor.more', 'more')) + '</div>');
        }
      }
      walk(this.root, 0);
      this._treeRows = rows;
      this.ui.tree.html(html.join(''));
    },

    updateTreeSelection: function () {
      var self = this;
      if (this.revealSelection()) {
        this.scheduleTree();
        return;
      }
      this.ui.tree.find('.ve-node-row').each(function () {
        var el = self._treeRows[parseInt(this.getAttribute('data-idx'), 10)];
        $(this).toggleClass('ve-selected', _.contains(self.selection, el));
      });
    },

    onTreeClick: function (e) {
      var $row = $(e.currentTarget), el = this._treeRows[parseInt($row.attr('data-idx'), 10)], btn = $(e.target).closest('[data-btn]').attr('data-btn');
      if (!el) { return; }
      if (btn === 'hide') {
        this.toggleHidden(el);
        return;
      }
      if (btn === 'lock') {
        this.toggleLock([el]);
        return;
      }
      if ($(e.target).closest('[data-caret]').length && CONTAINER[el.localName]) {
        this.expanded = _.contains(this.expanded, el) ? _.without(this.expanded, el) : this.expanded.concat([el]);
        this.renderTree();
        return;
      }
      this.level = this.containerOf(el);
      if ((e.shiftKey || e.metaKey || e.ctrlKey) && this.selection.length && this.selection[0].parentNode === el.parentNode) {
        this.setSelection(_.contains(this.selection, el) ? _.without(this.selection, el) : this.selection.concat([el]));
      } else {
        this.setSelection([el]);
      }
    },

    /* ------------------------------------------------------------------ */
    /* Context menu                                                         */
    /* ------------------------------------------------------------------ */

    showCtx: function (x, y) {
      var menu = this.ui.ctx, sel = this.editable(), all = this.selection, left, top, mw, mh;
      var hasGroup = _.some(sel, function (el) { return el.localName === 'g'; });
      this.ui.ctxLockLabel.text(_.every(all, isLocked) ? t('vectorEditor.unlock', 'Unlock') : t('vectorEditor.lock', 'Lock'));
      menu.find('[data-ctx="ungroup"]').toggleClass('ve-ctx-disabled', !hasGroup);
      menu.find('[data-ctx="ungroup-all"]').toggleClass('ve-ctx-disabled', !this.root.querySelector('g'));
      menu.find('[data-ctx="group"],[data-ctx="duplicate"],[data-ctx="delete"],[data-ctx="to-front"],[data-ctx="forward"],[data-ctx="backward"],[data-ctx="to-back"],[data-ctx="cut"],[data-ctx="copy"],[data-ctx="lock"]')
        .toggleClass('ve-ctx-disabled', !sel.length);
      menu.find('[data-ctx="text-outline"]').toggleClass('ve-ctx-disabled', !this.hasText());
      menu.find('[data-ctx="clip-make"]').toggleClass('ve-ctx-disabled', !this.canMakeClip());
      menu.find('[data-ctx="clip-release"]').toggleClass('ve-ctx-disabled', !this.canReleaseClip());
      menu.find('[data-ctx="center-h"],[data-ctx="center-v"],[data-ctx="center-both"],[data-ctx="fit-artboard"],[data-ctx="rotate-left"],[data-ctx="rotate-right"],[data-ctx="flip-h"],[data-ctx="flip-v"]')
        .toggleClass('ve-ctx-disabled', !sel.length);
      menu.find('[data-ctx="paste"]').toggleClass('ve-ctx-disabled', !clipboardStore);
      menu.find('[data-ctx="edit-image"]').toggleClass('ve-ctx-disabled', !this.imagesInSel().length);
      menu.find('.ve-ctx-opacity').toggleClass('ve-ctx-disabled', !sel.length);
      menu.css({ display: 'block', left: 0, top: 0 });
      mw = menu.outerWidth();
      mh = menu.outerHeight();
      left = Math.max(4, Math.min(x, window.innerWidth - mw - 8));
      top = Math.max(4, Math.min(y, window.innerHeight - mh - 8));
      menu.toggleClass('ve-ctx-flip', left + mw + 230 > window.innerWidth);
      menu.css({ left: left, top: top });
    },

    hideCtx: function () { this.ui.ctx.css('display', 'none'); },

    onCtx: function (e) {
      var $b = $(e.currentTarget), a = $b.attr('data-ctx');
      if ($b.hasClass('ve-ctx-disabled')) { return; }
      this.hideCtx();
      if (a === 'lock') {
        if (this.selection.length) { this.toggleLock(this.selection); }
        return;
      }
      this.runAction(a);
    },

    /* ------------------------------------------------------------------ */
    /* Export                                                               */
    /* ------------------------------------------------------------------ */

    exportClone: function () {
      var c = this.root.cloneNode(true), all, i, j, el, names;
      all = [c].concat(Array.prototype.slice.call(c.querySelectorAll('*')));
      for (i = 0; i < all.length; i++) {
        el = all[i];
        names = [];
        for (j = 0; j < el.attributes.length; j++) {
          if (/^data-ve-(?!line)/.test(el.attributes[j].name)) { names.push(el.attributes[j].name); }
        }
        for (j = 0; j < names.length; j++) { el.removeAttribute(names[j]); }
      }
      c.setAttribute('viewBox', [this.vb.x, this.vb.y, this.vb.w, this.vb.h].map(fmt).join(' '));
      if (!this.vbDirty && this.docW && this.docH) {
        c.setAttribute('width', this.docW);
        c.setAttribute('height', this.docH);
      } else {
        c.setAttribute('width', this.lenOut(this.vb.w));
        c.setAttribute('height', this.lenOut(this.vb.h));
      }
      c.style.removeProperty('overflow');
      c.style.removeProperty('display');
      if (!c.getAttribute('style')) { c.removeAttribute('style'); }
      c.setAttribute('xmlns', SVG_NS);
      c.setAttributeNS('http://www.w3.org/2000/xmlns/', 'xmlns:xlink', XLINK_NS);
      return c;
    },

    exportSvgString: function (clone) {
      return '<?xml version="1.0" encoding="UTF-8"?>\n' + new XMLSerializer().serializeToString(clone || this.exportClone());
    },

    outName: function (ext) {
      var base = (this.options.fileName || 'vector').replace(/\.[^.]+$/, '');
      return base + '-edited-' + Date.now() + '.' + ext;
    },

    makeFile: function (blob, name, type) {
      var file;
      try {
        file = new File([blob], name, { type: type });
      } catch (err) {
        blob.name = name;
        file = blob;
      }
      return file;
    },

    finish: function (file) {
      if (typeof this.options.onSave === 'function') { this.options.onSave(file); }
      if (this.windowView) { this.windowView.close(); }
    },

    // Text that comes from a PDF only exists as codes of the PDF's embedded font. Browsers can still draw it
    // through @font-face, but Illustrator, Inkscape, Preview … cannot, so it would look "damaged" there.
    // The saved file therefore gets those texts as outlines (the editor itself is left untouched).
    saveSvg: function () {
      var self = this, css = this.fontStyle ? this.fontStyle.textContent : '';
      if (!this.root) { return; }
      function write(clone) {
        var blob = new Blob([self.exportSvgString(clone)], { type: 'image/svg+xml' });
        self.finish(self.makeFile(blob, self.outName('svg'), 'image/svg+xml'));
      }
      if (!/@font-face/.test(css) || !this.hasText()) {
        write();
        return;
      }
      this.$el.addClass('ve-saving');
      loadGlobalScripts(window.opentype ? [] : [OPENTYPE_URL]).then(function () {
        var clone = self.exportClone(), res = outlineEmbeddedText(clone, parseEmbeddedFonts(css));
        if (res.done) {
          pruneFontFaces(clone);
          self.notify(t('vectorEditor.svgOutlined', 'Text from the PDF was saved as outlines so the SVG looks the same in every program.'));
        }
        write(clone);
      }, function () {
        self.$el.removeClass('ve-saving');
        self.notify(t('vectorEditor.outlineLib', 'The font reader could not be loaded.'));
        write();
      });
    },

    // Render a copy offscreen, bake every inherited / CSS-driven paint property
    // into plain attributes (svg2pdf does not evaluate style sheets), then hand
    // it to svg2pdf + jsPDF.
    savePdf: function () {
      var self = this, holder, shadow, svg, all, i, j, el, cs, v, w, h, wPt, hPt, doc, fn, opts, embedded;
      if (!this.root) { return; }
      this.$el.addClass('ve-saving');
      holder = document.createElement('div');
      holder.style.cssText = 'position:fixed;left:-100000px;top:0;visibility:hidden;pointer-events:none;';
      document.body.appendChild(holder);
      embedded = /@font-face/.test(self.fontStyle ? self.fontStyle.textContent : '');
      ensurePdfLibs(embedded).then(function () {
        svg = self.exportClone();
        w = self.vb.w;
        h = self.vb.h;
        svg.setAttribute('width', fmt(w));
        svg.setAttribute('width', fmt(w));
        svg.setAttribute('height', fmt(h));
        _.each(svg.querySelectorAll('[display="none"]'), function (n) { if (n.parentNode) { n.parentNode.removeChild(n); } });
        if (embedded && window.opentype) { outlineEmbeddedText(svg, parseEmbeddedFonts(self.fontStyle.textContent)); }
        shadow = holder.attachShadow({ mode: 'open' });
        shadow.appendChild(svg);
        all = Array.prototype.slice.call(svg.querySelectorAll('*'));
        for (i = 0; i < all.length; i++) {
          el = all[i];
          cs = window.getComputedStyle(el);
          if (SHAPE[el.localName]) {
            for (j = 0; j < BAKE.length; j++) {
              v = cs.getPropertyValue(BAKE[j]);
              if (BAKE[j] === 'font-family') { v = pdfFont(v); }
              if (v && v !== 'none') { el.setAttribute(BAKE[j], v.replace(/url\(\s*["']?(#[^"')]+)["']?\s*\)/, 'url($1)')); }
            }
          }
          if (el.localName === 'stop') {
            el.setAttribute('stop-color', cs.getPropertyValue('stop-color'));
            el.setAttribute('stop-opacity', cs.getPropertyValue('stop-opacity'));
          }
          if (RENDER[el.localName] && cs.opacity !== '1') { el.setAttribute('opacity', cs.opacity); }
          el.removeAttribute('style');
        }
        _.each(svg.querySelectorAll('style'), function (n) { n.parentNode.removeChild(n); });
        // svg2pdf resolves url(#id) references through the document → use light DOM.
        svg.removeAttribute('style');
        holder.appendChild(svg);
        wPt = w * self.mmPerUnit * 72 / 25.4;
        hPt = h * self.mmPerUnit * 72 / 25.4;
        doc = new window.jspdf.jsPDF({ unit: 'pt', format: [wPt, hPt], orientation: wPt > hPt ? 'landscape' : 'portrait' });
        opts = { x: 0, y: 0, width: wPt, height: hPt };
        fn = typeof doc.svg === 'function' ? function () { return doc.svg(svg, opts); } :
          function () { return (window.svg2pdf.svg2pdf || window.svg2pdf)(svg, doc, opts); };
        return Promise.resolve(fn());
      }).then(function () {
        var blob = doc.output('blob');
        self.finish(self.makeFile(blob, self.outName('pdf'), 'application/pdf'));
      }).then(null, function (err) {
        console.error('Vector editor PDF export failed', err);
        self.notify(t('vectorEditor.pdfError', 'The PDF could not be created.') + ' ' + ((err && err.message) || ''));
      }).then(function () {
        self.$el.removeClass('ve-saving');
        if (holder.parentNode) { holder.parentNode.removeChild(holder); }
      });
    },

    cancel: function () {
      if (this.windowView) { this.windowView.close(); }
    }
  });

  View.open = function (data) {
    require([
      'windows',
      'vectorEditorView'
    ], function (Windows, ViewCtor) {
      Windows.open({
        view: function () { return new ViewCtor(data); },
        id: 'vector-editor:' + (data && data.id ? data.id : Date.now()),
        title: (data && data.title) || t('vectorEditor.title', 'Vector editor'),
        modal: true,
        bodyClass: 'vector-editor-body',
        width: '92%',
        height: '92%'
      });
    });
  };

  Module.exports = View;
});

